import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { useArchitectureApi } from 'providers/ArchitectureApiProvider'
import Spinner from './Spinner'
import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  ArchClient,
  ArchCourse,
  ArchIntake,
  ArchModule,
  ArchProgram,
  ArchTopic,
} from 'apis/entities/architecture.entity'
import { ToastUtil } from 'utils/ToastUtil'
import { Role, useCompositeAuth } from 'providers/CompositeAuthProvider'
import {
  focusAndSelectField,
  InlineEditButtons,
} from 'components/InlineEditControls'
import Svg, { Icon, IconClass } from 'components/Svg'
import { tooltipStyle } from 'utils/TableUtils'
import { Tooltip } from 'react-tooltip'
import Checkbox, { CheckboxSize, CheckboxTheme } from 'components/Checkbox'
import { StyleUtil } from 'utils/StyleUtil'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import ButtonSpinner from './ButtonSpinner'
import {
  ArchActionType,
  getCourseByModuleId,
  getCourseByQuizId,
  getCourseCountByIntakeId,
  getDeleteConfirmationMessage,
  getDraggingStyle,
  getEntityLabel,
  getIconClasses,
  getIntakeCountByProgramId,
  getModuleByTopicId,
  getModuleCountByCourseId,
  getTopicCountByModuleId,
  getTooltipConfig,
  isCourseAssessmentItemsVisible,
  isCourseChildHeaderVisible,
  isCourseModuleItemsVisible,
  nestingPadding,
} from 'utils/ArchUtil'
import Emitter, { Events } from 'core/emitter'
import { TextareaAutosize } from '@mui/base'
import { Quiz } from 'apis/entities/quiz.entity'
import { getDateTimeString } from 'utils/StringUtil'
import { QuizStatusText } from './QuizStatusText'
import { ProgramRow } from './ProgramRow'
import { IntakeRow } from './IntakeRow'
import { getTopicUpdatedMessage } from 'utils/TopicToastUtil'

type ActionButtonName =
  | 'upload'
  | 'add'
  | 'copy'
  | 'delete'
  | 'collapse'
  | 'up'
  | 'down'
  | 'move'
type ActionButton = ActionButtonName | 'spacer'
type ActionButtons = ActionButton[]
type ActionButtonConfig = {
  typeNameForAdd: string
  typeName: string
  actionButtons: ActionButtons
}

const ACTION_BUTTON_SPACER: ActionButton = 'spacer'
const LOCKED_ACTIONS_BY_TYPE: Partial<
  Record<ArchActionType, readonly ActionButtonName[]>
> = {
  [ArchActionType.Program]: ['copy', 'collapse'],
  [ArchActionType.Intake]: ['copy', 'collapse'],
  [ArchActionType.Course]: ['copy', 'collapse'],
  [ArchActionType.CourseModules]: ['collapse'],
  [ArchActionType.CourseAssessments]: ['collapse'],
  [ArchActionType.CourseAssessmentsTests]: ['collapse'],
  [ArchActionType.Module]: ['copy', 'collapse'],
  [ArchActionType.Topic]: ['copy'],
  [ArchActionType.Quiz]: ['copy'],
}

const maskLockedActionButtons = (
  type: ArchActionType,
  actionButtons: ActionButtons,
): ActionButtons => {
  const allowedActions = LOCKED_ACTIONS_BY_TYPE[type]

  if (!allowedActions) {
    return actionButtons
  }

  const allowedActionSet = new Set<ActionButtonName>(allowedActions)
  return actionButtons.map((actionButton) => {
    if (actionButton === ACTION_BUTTON_SPACER) {
      return actionButton
    }

    return allowedActionSet.has(actionButton)
      ? actionButton
      : ACTION_BUTTON_SPACER
  })
}

interface DragAndDropData {
  moduleId: string
  topicId?: string
  // for moving to another module
  previousModuleId?: string
  nextModuleId?: string
}

// a dictionary to store the position of the data, [index] = DragAndDropData
interface DragAndDropDataDict {
  [index: string]: DragAndDropData
}

interface Props {
  client: ArchClient | null
  isLoading: boolean
  filteredProgramName?: string
  filteredIntakeName?: string
  onUpdateProgram?: () => void
  onUpdateIntake?: () => void
  isEnterprise?: boolean
}

// Helper function to generate tooltip IDs and display names for a given type
// Maps internal type names to user-friendly display names (e.g., "intake" -> "cohort")

const UNSWO_HEADER_BG = '#FFF5B2' // UNSW Online light yellow
const UNSWO_ROOT_BG = '#FFDC00' // UNSW Online deep yellow (root row)

type AddEntityInputProps = {
  entityName: string
  label?: string
  isAdding: boolean
  onConfirm: (name: string) => void
  onCancel: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  testId?: string
  inputClassName?: string
  wrapperStyle?: React.CSSProperties
}

type InlineSaveSource = 'autosave' | 'explicit'

type InlineSaveOptions = {
  source?: InlineSaveSource
}

type InlineEditCommitOptions = {
  timeoutKey: string
  nextValue: string
  previousValue: string
  emptyWarning: string
  save: (value: string, options?: InlineSaveOptions) => Promise<void> | void
  exitEditMode: () => void
}

type InlineEditAutosaveOptions = {
  timeoutKey: string
  nextValue: string
  previousValue: string
  save: (value: string, options?: InlineSaveOptions) => Promise<void> | void
}

type InlineEditCancelOptions = {
  timeoutKey: string
  textareaKey: string
  previousValue: string
  exitEditMode: () => void
}

const AddEntityInput = ({
  entityName,
  label,
  isAdding,
  onConfirm,
  onCancel,
  onKeyDown,
  testId,
  inputClassName,
  wrapperStyle,
}: AddEntityInputProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleConfirm = () => {
    const name = textareaRef.current?.value
    if (!name) {
      ToastUtil.warning(`${entityName} name cannot be empty`)
      return
    }
    onConfirm(name)
  }

  return (
    <div style={wrapperStyle}>
      {label && <span className="flex-none pr-1">{label}</span>}
      <TextareaAutosize
        ref={textareaRef}
        {...(testId && { 'data-testid': testId })}
        placeholder={`Type ${entityName.toLowerCase()} name here`}
        className={`arch-add-input ${inputClassName || ''} w-full`}
        autoFocus
        onKeyDown={onKeyDown}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            handleConfirm()
          }
        }}
        disabled={isAdding}
      />
      {isAdding && <ButtonSpinner />}
      {!isAdding && (
        <>
          <button
            className="ml-1 text-green-500 hover:text-green-700"
            onClick={handleConfirm}
          >
            <CheckIcon fontSize="small" />
          </button>
          <button
            className="ml-1 text-red-400 hover:text-red-600"
            onClick={onCancel}
          >
            <CloseIcon fontSize="small" />
          </button>
        </>
      )}
    </div>
  )
}

export default function ArchManageView({
  client,
  isLoading,
  filteredProgramName,
  filteredIntakeName,
  onUpdateProgram,
  onUpdateIntake,
  isEnterprise = true,
}: Props) {
  const unswoHeaderStyle = !isEnterprise
    ? { background: UNSWO_HEADER_BG, color: '#000', borderColor: 'white' }
    : undefined
  const unswoModuleStyle = !isEnterprise
    ? { background: 'white', color: '#000' }
    : undefined
  const checkboxTheme = !isEnterprise
    ? CheckboxTheme.Teal
    : CheckboxTheme.Purple
  const checkboxThemeOnHeader = !isEnterprise
    ? CheckboxTheme.Teal
    : CheckboxTheme.White
  const { isLogged, getRole } = useCompositeAuth()
  const role = getRole()
  const isSuperAdmin = role === Role.SuperAdmin
  const canCreateDeleteClone = isSuperAdmin

  const renderCheckboxOrPlaceholder = (
    locked: boolean,
    checkbox: React.ReactNode,
  ) =>
    locked || !canCreateDeleteClone ? (
      <div className="arch-checkbox-placeholder" aria-hidden="true" />
    ) : (
      checkbox
    )
  const {
    getArchClient,
    createArchProgram,
    createArchIntakeWithCourse,
    createArchCourse,
    createArchModule,
    createArchTopic,
    deleteArchProgram,
    deleteArchIntake,
    deleteArchCourse,
    deleteArchModule,
    deleteArchTopic,
    updateArchProgram,
    updateArchIntake,
    updateArchCourse,
    updateArchModule,
    updateArchTopic,
    moveArchTopic,
    toggleLockedForIntake,
    unassignQuizFromCourse,
  } = useArchitectureApi()
  const [isFetchingClient, setIsFetchingClient] = useState(false)
  const [selectedClient, setSelectedClient] = useState<ArchClient | null>(null)
  const [collapsedClientIds, setCollapsedClientIds] = useState<string[]>([])
  const [collapsedProgramIds, setCollapsedProgramIds] = useState<string[]>([])
  const [collapsedIntakeIds, setCollapsedIntakeIds] = useState<string[]>([])
  const [collapsedCourseIds, setCollapsedCourseIds] = useState<string[]>([])
  const [collapsedCourseModulesIds, setCollapsedCourseModulesIds] = useState<
    string[]
  >([])
  const [collapsedCourseAssessmentsIds, setCollapsedCourseAssessmentsIds] =
    useState<string[]>([])
  const [
    collapsedCourseAssessmentsTestsIds,
    setCollapsedCourseAssessmentsTestsIds,
  ] = useState<string[]>([])
  const [collapsedModuleIds, setCollapsedModuleIds] = useState<string[]>([])
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  let draggableIndex = 0

  const [checkedCourseIds, setCheckedCourseIds] = useState<string[]>([])
  // for all modules in the course
  const [checkedCourseModulesIds, setCheckedCourseModulesIds] = useState<
    string[]
  >([])
  // for all assessments in the course
  const [checkedCourseAssessmentsIds, setCheckedCourseAssessmentsIds] =
    useState<string[]>([])
  const [checkedModuleIds, setCheckedModuleIds] = useState<string[]>([])
  const [checkedTopicIds, setCheckedTopicIds] = useState<string[]>([])
  const [checkedQuizIds, setCheckedQuizIds] = useState<string[]>([])
  const [checkboxSelectAll, setCheckboxSelectAll] = useState(false)

  // for single delete button
  const [singleDelete, setSingleDelete] = useState<{
    id: string | null
    type: ArchActionType | null
  }>({ id: null, type: null })
  const [isDeleting, setIsDeleting] = useState(false)

  // for client to add program
  const [showAddProgram, setShowAddProgram] = useState(false)
  const [isAddingProgram, setIsAddingProgram] = useState(false)
  // programIds for adding intake
  const [showAddIntakeIds, setShowAddIntakeIds] = useState<string[]>([])
  const [isAddingIntakeIds, setIsAddingIntakeIds] = useState<string[]>([])
  // intakeIds for adding course
  const [showAddCourseIds, setShowAddCourseIds] = useState<string[]>([])
  const [isAddingCourseIds, setIsAddingCourseIds] = useState<string[]>([])
  // courseId for adding module
  const [showAddModuleIds, setShowAddModuleIds] = useState<string[]>([])
  const [isAddingModuleIds, setIsAddingModuleIds] = useState<string[]>([])
  // moduleId for adding topic
  const [showAddTopicIds, setShowAddTopicIds] = useState<string[]>([])
  const [isAddingTopicIds, setIsAddingTopicIds] = useState<string[]>([])

  const refDragAndDropDataDict = useRef<DragAndDropDataDict>({})
  const refEditTextareas = useRef<Map<string, HTMLTextAreaElement>>(new Map())
  const refTimeoutDict = useRef<{ [index: string]: NodeJS.Timeout }>({})
  const timeoutForSave = 500

  const refclonedObject = useRef<{
    [key: string]: string | string[] | undefined
  }>({})

  // store the locking status of the intake
  const [lockLoading, setLockLoading] = useState<{
    [intakeId: string]: boolean
  }>({})

  enum EditPrefix {
    ProgramName = 'program-name-',
    IntakeName = 'intake-name-',
    CourseName = 'course-name-',
    ModuleName = 'module-name-',
    TopicLearningOutcome = 'topic-learning-outcome-',
    TopicDuration = 'topic-duration-',
  }

  const [editedModeCells, setEditedModeCells] = useState<{
    [id: string]: Boolean
  }>({})

  const onKeyDownForTextArea = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
    }
    // reset clonedObject
    refclonedObject.current = {}
  }

  const onClickOrBlurTextArea = (
    prefix: string,
    targetId: string,
    value: boolean,
  ) => {
    setEditedModeCells((previous) => ({
      ...previous,
      [`${prefix}-${targetId}`]: value,
    }))
    // reset clonedObject
    refclonedObject.current = {}
  }

  const readOnlyMode = (prefix: string, targetId: string): boolean => {
    if (
      prefix === EditPrefix.ProgramName &&
      refclonedObject.current['programId'] === targetId
    ) {
      return false
    } else if (
      prefix === EditPrefix.IntakeName &&
      refclonedObject.current['intakeId'] === targetId
    ) {
      return false
    }
    return !editedModeCells[`${prefix}-${targetId}`]
  }

  const canAutoFocus = (prefix: string, targetId: string): boolean => {
    if (
      prefix === EditPrefix.ProgramName &&
      refclonedObject.current['programId'] === targetId
    ) {
      return true
    } else if (
      prefix === EditPrefix.IntakeName &&
      refclonedObject.current['intakeId'] === targetId
    ) {
      return true
    }
    return false
  }

  const resetTimeout = (
    index: string,
    timeout?: NodeJS.Timeout | undefined,
  ) => {
    if (refTimeoutDict.current[index]) {
      clearTimeout(refTimeoutDict.current[index])
      delete refTimeoutDict.current[index]
    }
    if (timeout !== undefined && timeout !== null) {
      refTimeoutDict.current[index] = timeout
    }
  }

  const commitInlineEdit = ({
    timeoutKey,
    nextValue,
    previousValue,
    emptyWarning,
    save,
    exitEditMode,
  }: InlineEditCommitOptions) => {
    const trimmedValue = nextValue.trim()
    if (!trimmedValue) {
      ToastUtil.warning(emptyWarning)
      return
    }
    resetTimeout(timeoutKey)
    if (trimmedValue !== previousValue) {
      void save(trimmedValue, { source: 'explicit' })
    }
    exitEditMode()
  }

  const scheduleInlineAutosave = ({
    timeoutKey,
    nextValue,
    previousValue,
    save,
  }: InlineEditAutosaveOptions) => {
    const trimmedValue = nextValue.trim()
    if (trimmedValue === previousValue) {
      resetTimeout(timeoutKey)
      return
    }
    const timeoutId = setTimeout(() => {
      void save(trimmedValue, { source: 'autosave' })
    }, timeoutForSave)
    resetTimeout(timeoutKey, timeoutId)
  }

  const cancelInlineEdit = ({
    timeoutKey,
    textareaKey,
    previousValue,
    exitEditMode,
  }: InlineEditCancelOptions) => {
    const el = refEditTextareas.current.get(textareaKey)
    if (el) {
      el.value = previousValue
    }
    resetTimeout(timeoutKey)
    exitEditMode()
  }

  const emitReloadClient = useCallback(
    (updateCourseTable: boolean = false) => {
      if (!selectedClient) return
      Emitter.emit(Events.ReloadArchClient, {
        clientId: selectedClient.id,
        updateCourseTable: updateCourseTable,
      })
    },
    [selectedClient],
  )

  const updateDragAndDropDataDict = () => {
    // console.log('before update', refDragAndDropDataDict.current)

    // get the moduleIds
    const indexes = Object.keys(refDragAndDropDataDict.current).filter(
      (index) => refDragAndDropDataDict.current[index].topicId === undefined,
    )
    const moduleIds = indexes.map(
      (index) => refDragAndDropDataDict.current[index].moduleId,
    )
    // console.log('moduleIds', moduleIds)
    // set previousModuleId and nextModuleId for each module
    moduleIds.forEach((moduleId, index) => {
      const previousModuleId = index > 0 ? moduleIds[index - 1] : undefined
      const nextModuleId =
        index < moduleIds.length - 1 ? moduleIds[index + 1] : undefined
      const dictIndex = indexes.find(
        (i) => refDragAndDropDataDict.current[i].moduleId === moduleId,
      )
      if (dictIndex) {
        refDragAndDropDataDict.current[dictIndex].previousModuleId =
          previousModuleId
        refDragAndDropDataDict.current[dictIndex].nextModuleId = nextModuleId
      }
    })
    // console.log('after update', refDragAndDropDataDict.current)
  }

  const updateProgramName = useCallback(
    async (
      programId: string,
      name: string,
      options: InlineSaveOptions = {},
    ) => {
      try {
        if (!name) {
          ToastUtil.warning(
            `${getEntityLabel(
              ArchActionType.Program,
              !isEnterprise,
            )} name cannot be empty`,
          )
          return
        }
        await updateArchProgram(programId, name)
        if (options.source !== 'autosave') {
          ToastUtil.success(
            `${getEntityLabel(
              ArchActionType.Program,
              !isEnterprise,
            )} "${name}" updated`,
          )
        }
        emitReloadClient(true)
      } catch (error) {
        ToastUtil.error(
          `${getEntityLabel(
            ArchActionType.Program,
            !isEnterprise,
          )} update failed`,
        )
        console.error(error)
      }
    },
    [emitReloadClient, isEnterprise, updateArchProgram],
  )

  const updateIntakeName = useCallback(
    async (intakeId: string, name: string, options: InlineSaveOptions = {}) => {
      try {
        if (!name) {
          ToastUtil.warning(
            `${getEntityLabel(
              ArchActionType.Intake,
              !isEnterprise,
            )} name cannot be empty`,
          )
          return
        }
        await updateArchIntake(intakeId, name)
        if (options.source !== 'autosave') {
          ToastUtil.success(
            `${getEntityLabel(
              ArchActionType.Intake,
              !isEnterprise,
            )} "${name}" updated`,
          )
        }
        emitReloadClient(true)
      } catch (error) {
        ToastUtil.error(
          `${getEntityLabel(
            ArchActionType.Intake,
            !isEnterprise,
          )} update failed`,
        )
        console.error(error)
      }
    },
    [emitReloadClient, isEnterprise, updateArchIntake],
  )

  const updateCourseName = useCallback(
    async (courseId: string, name: string, options: InlineSaveOptions = {}) => {
      try {
        if (!name) {
          ToastUtil.warning('Course name cannot be empty')
          return
        }
        await updateArchCourse(courseId, name)
        if (options.source !== 'autosave') {
          ToastUtil.success(`Course "${name}" updated`)
        }
        emitReloadClient(true)
      } catch (error) {
        ToastUtil.error('Course update failed')
        console.error(error)
      }
    },
    [emitReloadClient, updateArchCourse],
  )

  const updateModuleName = useCallback(
    async (moduleId: string, name: string, options: InlineSaveOptions = {}) => {
      try {
        if (!name) {
          ToastUtil.warning(
            `${getEntityLabel(
              ArchActionType.Module,
              !isEnterprise,
            )} name cannot be empty`,
          )
          return
        }
        await updateArchModule(moduleId, name)
        if (options.source !== 'autosave') {
          ToastUtil.success(
            `${getEntityLabel(
              ArchActionType.Module,
              !isEnterprise,
            )} "${name}" updated`,
          )
        }
      } catch (error) {
        ToastUtil.error(
          `${getEntityLabel(
            ArchActionType.Module,
            !isEnterprise,
          )} update failed`,
        )
        console.error(error)
      }
    },
    [isEnterprise, updateArchModule],
  )

  const updateTopicLearningOutcome = useCallback(
    async (
      topicId: string,
      topicName: string,
      learningOutcome: string,
      options: InlineSaveOptions = {},
    ) => {
      try {
        const outcomeLabel = isEnterprise ? 'learning outcome' : 'purpose'
        await updateArchTopic(topicId, undefined, learningOutcome)
        if (options.source !== 'autosave') {
          ToastUtil.success(
            getTopicUpdatedMessage(`"${topicName}" ${outcomeLabel}`),
          )
        }
      } catch (error) {
        ToastUtil.error('Topic update failed')
        console.error(error)
      }
    },
    [updateArchTopic, isEnterprise],
  )

  const updateTopicDuration = useCallback(
    async (topicId: string, minutes: string) => {
      try {
        const parsed = parseInt(minutes, 10)
        if (isNaN(parsed) || parsed < 0) {
          ToastUtil.warning('Duration must be a valid number of minutes')
          return
        }
        await updateArchTopic(
          topicId,
          undefined,
          undefined,
          undefined,
          parsed * 60,
        )
        ToastUtil.success(getTopicUpdatedMessage('duration'))
      } catch (error) {
        ToastUtil.error('Topic update failed')
        console.error(error)
      }
    },
    [updateArchTopic],
  )

  const checkedActionType = (): ArchActionType | null => {
    if (checkedCourseIds.length > 0) {
      return ArchActionType.Course
    }
    if (checkedModuleIds.length > 0) {
      return ArchActionType.Module
    }
    if (checkedTopicIds.length > 0) {
      return ArchActionType.Topic
    }
    if (checkedQuizIds.length > 0) {
      return ArchActionType.Quiz
    }
    return null
  }

  const renderAllSelectText = () => {
    const actionType = checkedActionType()
    if (actionType === ArchActionType.Course) {
      return `Select all courses`
    } else if (actionType === ArchActionType.Module) {
      return `Select all modules`
    } else if (actionType === ArchActionType.Topic) {
      return `Select all topics`
    } else if (actionType === ArchActionType.Quiz) {
      return `Select all assessments`
    }
    return ''
  }

  const renderDeleteFromText = () => {
    const actionType = checkedActionType()
    if (actionType === ArchActionType.Course) {
      return `Delete from ${getEntityLabel(
        ArchActionType.Intake,
        !isEnterprise,
      ).toLowerCase()}`
    } else if (actionType === ArchActionType.Module) {
      return `Delete from course`
    } else if (actionType === ArchActionType.Topic) {
      return `Delete from ${getEntityLabel(
        ArchActionType.Module,
        !isEnterprise,
      ).toLowerCase()}`
    } else if (actionType === ArchActionType.Quiz) {
      return `Delete from course`
    }
    return ''
  }

  const renderDuplicateToText = () => {
    const actionType = checkedActionType()
    if (actionType === ArchActionType.Course) {
      return `Duplicate to other cohort`
    } else if (actionType === ArchActionType.Module) {
      return `Duplicate to other course`
    } else if (actionType === ArchActionType.Topic) {
      return `Duplicate to other module`
    } else if (actionType === ArchActionType.Quiz) {
      return `Duplicate to other course`
    }
    return ''
  }

  /**
   * sample 1: Are you sure you want to delete these 2 modules, including 6 topics?
   * sample 2: Are you sure you want to delete these 4 topics?
   * sample 3: Are you sure you want to delete these 6 topics?
   * sample 4: Are you sure you want to delete this course?
   * sample 5: Are you sure you want to delete this topic?
   * sample 6: Are you sure you want to delete these 2 courses, including 12 modules?
   * sample 7: Are you sure you want to delete this course, including 6 modules?
   * sample 8: Are you sure you want to delete these 5 assessments?
   * @returns delete confirmation text
   */
  const renderDeleteConfirmationText = (): string => {
    if (!selectedClient) {
      return ''
    }
    // check if single delete
    if (singleDelete !== null) {
      const { id, type } = singleDelete
      if (id && type) {
        if (type === ArchActionType.Program) {
          const intakeCount = getIntakeCountByProgramId(selectedClient, id)
          return getDeleteConfirmationMessage(
            getEntityLabel(ArchActionType.Program, !isEnterprise).toLowerCase(),
            1,
            getEntityLabel(ArchActionType.Intake, !isEnterprise).toLowerCase(),
            intakeCount,
          )
        } else if (type === ArchActionType.Intake) {
          const courseCount = getCourseCountByIntakeId(selectedClient, id)
          return getDeleteConfirmationMessage(
            getEntityLabel(ArchActionType.Intake, !isEnterprise).toLowerCase(),
            1,
            'course',
            courseCount,
          )
        } else if (type === ArchActionType.Course) {
          const moduleCount = getModuleCountByCourseId(selectedClient, id)
          return getDeleteConfirmationMessage(
            'course',
            1,
            getEntityLabel(ArchActionType.Module, !isEnterprise).toLowerCase(),
            moduleCount,
          )
        } else if (type === ArchActionType.Module) {
          const topicCount = getTopicCountByModuleId(selectedClient, id)
          return getDeleteConfirmationMessage(
            getEntityLabel(ArchActionType.Module, !isEnterprise).toLowerCase(),
            1,
            'topic',
            topicCount,
          )
        } else if (type === ArchActionType.Topic) {
          return `Are you sure you want to delete this topic?`
        } else if (type === ArchActionType.Quiz) {
          return `Are you sure you want to delete this assessment?`
        }
      }
    }

    const actionType = checkedActionType()
    if (actionType === ArchActionType.Course) {
      const courseCount = checkedCourseIds.length
      const moduleCount = selectedClient?.programs
        ?.map((program) =>
          program.intakes?.map((intake) =>
            intake.courses?.filter((course) =>
              checkedCourseIds.includes(course.id),
            ),
          ),
        )
        .flat(2)
        .filter(Boolean)
        .map((course) => course.modules?.length)
        .reduce((acc, val) => acc + val, 0)
      return getDeleteConfirmationMessage(
        'course',
        courseCount,
        getEntityLabel(ArchActionType.Module, !isEnterprise).toLowerCase(),
        moduleCount,
      )
    } else if (actionType === ArchActionType.Module) {
      const moduleCount = checkedModuleIds.length
      const topicCount = selectedClient?.programs
        ?.map((program) =>
          program.intakes?.map((intake) =>
            intake.courses?.map((course) =>
              course.modules?.map((module) =>
                module.topics?.filter((topic) =>
                  checkedModuleIds.includes(module.id),
                ),
              ),
            ),
          ),
        )
        .flat(4)
        .filter(Boolean).length
      return getDeleteConfirmationMessage(
        getEntityLabel(ArchActionType.Module, !isEnterprise).toLowerCase(),
        moduleCount,
        'topic',
        topicCount,
      )
    } else if (actionType === ArchActionType.Topic) {
      const topicCount = checkedTopicIds.length
      return getDeleteConfirmationMessage('topic', topicCount, '', 0)
    } else if (actionType === ArchActionType.Quiz) {
      const quizCount = checkedQuizIds.length
      return getDeleteConfirmationMessage('assessment', quizCount, '', 0)
    }
    return ''
  }

  const fetchClient = useCallback(
    async (id: string, silent: boolean = false) => {
      try {
        if (!silent) {
          setIsFetchingClient(true)
          setCollapsedProgramIds([])
          setCollapsedIntakeIds([])
          setCollapsedCourseIds([])
          setCollapsedModuleIds([])
          setCollapsedCourseModulesIds([])
          setCollapsedCourseAssessmentsIds([])
        }

        // reset everything
        setShowDeleteConfirmation(false)
        clearAllCheckboxes()
        setCheckboxSelectAll(false)
        // reset add input
        setShowAddProgram(false)
        setShowAddIntakeIds([])
        setShowAddCourseIds([])
        setShowAddModuleIds([])
        setShowAddTopicIds([])
        // reset single delete
        setSingleDelete({ id: null, type: null })

        const resultClient = await getArchClient(id)
        setSelectedClient(resultClient)
      } catch (error) {
        ToastUtil.error('Failed to get client')
      } finally {
        if (!silent) {
          setIsFetchingClient(false)
        }
      }
    },
    [getArchClient],
  )

  const createProgram = useCallback(
    async (programName: string) => {
      if (!selectedClient) {
        return
      }
      try {
        setIsAddingProgram(true)
        await createArchProgram(selectedClient.id, programName)
        await fetchClient(selectedClient.id, true)
        setShowAddProgram(false)
        if (onUpdateProgram) {
          onUpdateProgram()
        }
        emitReloadClient(true)
      } catch (error) {
        ToastUtil.error(
          `Failed to create ${getEntityLabel(
            ArchActionType.Program,
            !isEnterprise,
          ).toLowerCase()}`,
        )
        console.error(error)
      } finally {
        setIsAddingProgram(false)
      }
    },
    [
      selectedClient,
      createArchProgram,
      fetchClient,
      isEnterprise,
      onUpdateProgram,
      emitReloadClient,
    ],
  )

  const createIntake = useCallback(
    async (programId: string, intakeName: string) => {
      if (!selectedClient) {
        return
      }
      try {
        setIsAddingIntakeIds([...isAddingIntakeIds, programId])
        await createArchIntakeWithCourse(programId, intakeName)
        await fetchClient(selectedClient.id, true)
        setShowAddIntakeIds(showAddIntakeIds.filter((id) => id !== programId))
        if (onUpdateIntake) {
          onUpdateIntake()
        }
        emitReloadClient(true)
      } catch (error) {
        ToastUtil.error(
          `Failed to create ${getEntityLabel(
            ArchActionType.Intake,
            !isEnterprise,
          ).toLowerCase()}`,
        )
        console.error(error)
      } finally {
        setIsAddingIntakeIds(isAddingIntakeIds.filter((id) => id !== programId))
      }
    },
    [
      selectedClient,
      isAddingIntakeIds,
      createArchIntakeWithCourse,
      fetchClient,
      isEnterprise,
      showAddIntakeIds,
      onUpdateIntake,
      emitReloadClient,
    ],
  )

  const createCourse = useCallback(
    async (intakeId: string, courseName: string) => {
      if (!selectedClient) {
        return
      }
      try {
        setIsAddingCourseIds([...isAddingCourseIds, intakeId])
        await createArchCourse(intakeId, courseName)
        await fetchClient(selectedClient.id, true)
        setShowAddCourseIds(showAddCourseIds.filter((id) => id !== intakeId))
        emitReloadClient(true)
      } catch (error) {
        ToastUtil.error('Failed to create course')
        console.error(error)
      } finally {
        setIsAddingCourseIds(isAddingCourseIds.filter((id) => id !== intakeId))
      }
    },
    [
      createArchCourse,
      emitReloadClient,
      fetchClient,
      isAddingCourseIds,
      selectedClient,
      showAddCourseIds,
    ],
  )

  const createModule = useCallback(
    async (courseId: string, moduleName: string) => {
      if (!selectedClient) {
        return
      }
      try {
        setIsAddingModuleIds([...isAddingModuleIds, courseId])
        await createArchModule(courseId, moduleName)
        await fetchClient(selectedClient.id, true)
        setShowAddModuleIds(showAddModuleIds.filter((id) => id !== courseId))
      } catch (error) {
        ToastUtil.error(
          `Failed to create ${getEntityLabel(
            ArchActionType.Module,
            !isEnterprise,
          ).toLowerCase()}`,
        )
        console.error(error)
      } finally {
        setIsAddingModuleIds(isAddingModuleIds.filter((id) => id !== courseId))
      }
    },
    [
      createArchModule,
      fetchClient,
      isEnterprise,
      isAddingModuleIds,
      selectedClient,
      showAddModuleIds,
    ],
  )

  const createTopic = useCallback(
    async (moduleId: string, topicName: string) => {
      if (!selectedClient) {
        return
      }
      try {
        setIsAddingTopicIds([...isAddingTopicIds, moduleId])
        await createArchTopic(moduleId, topicName)
        await fetchClient(selectedClient.id, true)
        setShowAddTopicIds(showAddTopicIds.filter((id) => id !== moduleId))
      } catch (error) {
        ToastUtil.error('Failed to create topic')
        console.error(error)
      } finally {
        setIsAddingTopicIds(isAddingTopicIds.filter((id) => id !== moduleId))
      }
    },
    [
      createArchTopic,
      fetchClient,
      isAddingTopicIds,
      selectedClient,
      showAddTopicIds,
    ],
  )

  // only support Intake currently
  const onUploadExcel = (id: string, type: ArchActionType) => {
    if (type !== ArchActionType.Intake) return
    Emitter.emit(Events.UploadArchExcel, { intakeId: id })
  }

  const onAdd = (id: string, type: ArchActionType) => {
    switch (type) {
      case ArchActionType.Client:
        setShowAddProgram(true)
        break
      case ArchActionType.Program:
        setShowAddIntakeIds([...showAddIntakeIds, id])
        break
      case ArchActionType.Intake:
        setShowAddCourseIds([...showAddCourseIds, id])
        break
      case ArchActionType.Course:
      case ArchActionType.CourseModules:
        setShowAddModuleIds([...showAddModuleIds, id])
        break
      case ArchActionType.Module:
        setShowAddTopicIds([...showAddTopicIds, id])
        break
      case ArchActionType.CourseAssessments:
        Emitter.emit(Events.ShowUnassignedAssignments, { courseId: id })
        break
      case ArchActionType.CourseAssessmentsTests:
        Emitter.emit(Events.ShowUnassignedAssignments, { courseId: id })
        break
    }
  }

  const onDelete = (id: string, type: ArchActionType) => {
    // reset all checked ids
    clearAllCheckboxes()

    setSingleDelete({ id, type })
    setShowDeleteConfirmation(true)
  }

  const onConfirmDelete = async () => {
    if (!selectedClient) {
      return
    }
    setIsDeleting(true)

    try {
      const { id, type } = singleDelete
      if (id && type) {
        switch (type) {
          case ArchActionType.Program:
            await deleteArchProgram(id)
            emitReloadClient(true)
            break
          case ArchActionType.Intake:
            await deleteArchIntake(id)
            emitReloadClient(true)
            break
          case ArchActionType.Course:
            await deleteArchCourse(id)
            emitReloadClient(true)
            break
          case ArchActionType.Module:
            await deleteArchModule(id)
            break
          case ArchActionType.Topic:
            await deleteArchTopic(id)
            break
          case ArchActionType.Quiz:
            const course = getCourseByQuizId(selectedClient, id)
            if (!course) {
              ToastUtil.error('Course not found for quiz')
              return
            }
            await unassignQuizFromCourse([id], course.id)
            break
          default:
            ToastUtil.error('Invalid action type')
            return
        }
      } else {
        // multiple delete
        const actionType = checkedActionType()
        switch (actionType) {
          case ArchActionType.Course:
            await Promise.all(
              checkedCourseIds.map((courseId) => deleteArchCourse(courseId)),
            )
            break

          case ArchActionType.Module:
            await Promise.all(
              checkedModuleIds.map((moduleId) => deleteArchModule(moduleId)),
            )
            break

          case ArchActionType.Topic:
            await Promise.all(
              checkedTopicIds.map((topicId) => deleteArchTopic(topicId)),
            )
            break

          case ArchActionType.Quiz:
            await Promise.all(
              checkedQuizIds.map((quizId) => {
                const course = getCourseByQuizId(selectedClient, quizId)
                if (!course) {
                  throw new Error(`Course not found for quiz ${quizId}`)
                }
                return unassignQuizFromCourse([quizId], course.id)
              }),
            )
            break

          default:
            ToastUtil.error('Invalid action type')
            return
        }
      }

      await fetchClient(selectedClient.id, true)
      setShowDeleteConfirmation(false)
      ToastUtil.success('Successfully deleted')
    } catch (error) {
      const e = error as unknown as { message: string }
      const message = `Failed to delete: ${e.message}`
      ToastUtil.error(message)
      console.error(error)
    } finally {
      setIsDeleting(false)
    }
  }

  // single item action
  const onDuplicate = (id: string, type: ArchActionType) => {
    Emitter.emit(Events.ShowArchCopy, { type, ids: [id] })
  }

  // multiple items action
  const onDuplicateSelectedItems = () => {
    const actionType = checkedActionType()
    if (!selectedClient || !actionType) {
      return
    }
    let ids: string[] = []
    switch (actionType) {
      case ArchActionType.Course:
        ids = checkedCourseIds
        break
      case ArchActionType.Module:
        ids = checkedModuleIds
        break
      case ArchActionType.Quiz:
        ids = checkedQuizIds
        break
      default:
        ids = checkedTopicIds
        break
    }
    Emitter.emit(Events.ShowArchCopy, { type: actionType, ids })
  }

  // only support topic and module
  const onMoveUp = async (id: string, type: ArchActionType) => {
    if (!selectedClient) return
    if (type === ArchActionType.Topic) {
      // get module by topic id
      const targetModule = getModuleByTopicId(selectedClient, id)
      // console.log(module)
      if (!targetModule) return
      const topicIds = targetModule.topics.map((topic) => topic.id)
      const currentIndex = topicIds.indexOf(id)
      // skip the first item
      if (currentIndex === 0) return
      // swap the ids
      const temp = topicIds[currentIndex]
      topicIds[currentIndex] = topicIds[currentIndex - 1]
      topicIds[currentIndex - 1] = temp
      // TODO: update local state
      // save to database
      await updateArchModule(targetModule.id, undefined, topicIds)
      // TODO: remove this line for local change only
      await fetchClient(selectedClient.id, true)
    } else if (type === ArchActionType.Module) {
      // get course by module id
      const targetCourse = getCourseByModuleId(selectedClient, id)
      // console.log(targetCourse)
      if (!targetCourse) return
      const moduleIds = targetCourse.modules.map((module) => module.id)
      const currentIndex = moduleIds.indexOf(id)
      // skip the first item
      if (currentIndex === 0) {
        console.log('skip the first item')
        return
      }
      // swap the ids
      const temp = moduleIds[currentIndex]
      moduleIds[currentIndex] = moduleIds[currentIndex - 1]
      moduleIds[currentIndex - 1] = temp
      // TODO: update local state
      // save to database
      await updateArchCourse(targetCourse.id, undefined, undefined, moduleIds)
      // TODO: remove this line for local change only
      await fetchClient(selectedClient.id, true)
    }
  }

  // only support topic and module
  const onMoveDown = async (id: string, type: ArchActionType) => {
    if (!selectedClient) return
    if (type === ArchActionType.Topic) {
      // get module by topic id
      const targetModule = getModuleByTopicId(selectedClient, id)
      if (!targetModule) return
      const topicIds = targetModule.topics.map((topic) => topic.id)
      const currentIndex = topicIds.indexOf(id)
      // skip the last item
      if (currentIndex === topicIds.length - 1) return
      // swap the ids
      const temp = topicIds[currentIndex]
      topicIds[currentIndex] = topicIds[currentIndex + 1]
      topicIds[currentIndex + 1] = temp
      // TODO: update local state
      // save to database
      await updateArchModule(targetModule.id, undefined, topicIds)
      // TODO: remove this line for local change only
      await fetchClient(selectedClient.id, true)
    } else if (type === ArchActionType.Module) {
      // get course by module id
      const targetCourse = getCourseByModuleId(selectedClient, id)
      if (!targetCourse) return
      const moduleIds = targetCourse.modules.map((module) => module.id)
      const currentIndex = moduleIds.indexOf(id)
      // skip the last item
      if (currentIndex === moduleIds.length - 1) {
        console.log('skip the last item')
        return
      }
      // swap the ids
      const temp = moduleIds[currentIndex]
      moduleIds[currentIndex] = moduleIds[currentIndex + 1]
      moduleIds[currentIndex + 1] = temp
      // TODO: update local state
      // save to database
      await updateArchCourse(targetCourse.id, undefined, undefined, moduleIds)
      // TODO: remove this line for local change only
      await fetchClient(selectedClient.id, true)
    }
  }

  const onToggleCollapseClient = (id: string) => {
    if (collapsedClientIds.includes(id)) {
      setCollapsedClientIds(collapsedClientIds.filter((i) => i !== id))
    } else {
      setCollapsedClientIds([...collapsedClientIds, id])
    }
  }

  const onToggleCollapseProgram = (id: string) => {
    if (collapsedProgramIds.includes(id)) {
      setCollapsedProgramIds(collapsedProgramIds.filter((i) => i !== id))
    } else {
      setCollapsedProgramIds([...collapsedProgramIds, id])
    }
  }

  const onToggleCollapseIntake = (id: string) => {
    if (collapsedIntakeIds.includes(id)) {
      setCollapsedIntakeIds(collapsedIntakeIds.filter((i) => i !== id))
    } else {
      setCollapsedIntakeIds([...collapsedIntakeIds, id])
    }
  }

  const onToggleCollapseCourse = (id: string) => {
    if (collapsedCourseIds.includes(id)) {
      setCollapsedCourseIds(collapsedCourseIds.filter((i) => i !== id))
    } else {
      setCollapsedCourseIds([...collapsedCourseIds, id])
    }
  }

  const onToggleCollapseModule = (id: string) => {
    if (collapsedModuleIds.includes(id)) {
      setCollapsedModuleIds(collapsedModuleIds.filter((i) => i !== id))
    } else {
      setCollapsedModuleIds([...collapsedModuleIds, id])
    }
  }

  const onToggleCollapseCourseModules = (id: string) => {
    if (collapsedCourseModulesIds.includes(id)) {
      setCollapsedCourseModulesIds(
        collapsedCourseModulesIds.filter((i) => i !== id),
      )
    } else {
      setCollapsedCourseModulesIds([...collapsedCourseModulesIds, id])
    }
  }

  const onToggleCollapseCourseAssessments = (id: string) => {
    if (collapsedCourseAssessmentsIds.includes(id)) {
      setCollapsedCourseAssessmentsIds(
        collapsedCourseAssessmentsIds.filter((i) => i !== id),
      )
    } else {
      setCollapsedCourseAssessmentsIds([...collapsedCourseAssessmentsIds, id])
    }
  }

  const onToggleCollapseCourseAssessmentsTests = (id: string) => {
    if (collapsedCourseAssessmentsTestsIds.includes(id)) {
      setCollapsedCourseAssessmentsTestsIds(
        collapsedCourseAssessmentsTestsIds.filter((i) => i !== id),
      )
    } else {
      setCollapsedCourseAssessmentsTestsIds([
        ...collapsedCourseAssessmentsTestsIds,
        id,
      ])
    }
  }

  const onToggleCollapse = (id: string, type: ArchActionType) => {
    switch (type) {
      case ArchActionType.Client:
        onToggleCollapseClient(id)
        break
      case ArchActionType.Program:
        onToggleCollapseProgram(id)
        break
      case ArchActionType.Intake:
        onToggleCollapseIntake(id)
        break
      case ArchActionType.Course:
        onToggleCollapseCourse(id)
        break
      case ArchActionType.Module:
        onToggleCollapseModule(id)
        break
      case ArchActionType.CourseModules:
        onToggleCollapseCourseModules(id)
        break
      case ArchActionType.CourseAssessments:
        onToggleCollapseCourseAssessments(id)
        break
      case ArchActionType.CourseAssessmentsTests:
        onToggleCollapseCourseAssessmentsTests(id)
        break
    }
  }

  const isCollapsedProgram = (id: string) => {
    return collapsedProgramIds.includes(id)
  }

  const isCollapsedIntake = (id: string) => {
    return collapsedIntakeIds.includes(id)
  }

  const isCollapsedCourse = (id: string) => {
    return collapsedCourseIds.includes(id)
  }

  const isCollapsedModule = (id: string) => {
    return collapsedModuleIds.includes(id)
  }

  const isCollapsedCourseModules = (id: string) => {
    return collapsedCourseModulesIds.includes(id)
  }

  const isCollapsedCourseAssessments = (id: string) => {
    return collapsedCourseAssessmentsIds.includes(id)
  }

  const isCollapsedCourseAssessmentsTests = (id: string) => {
    return collapsedCourseAssessmentsTestsIds.includes(id)
  }

  const isCollapsed = (id: string, type: ArchActionType) => {
    // console.log('isCollapsed', id, type)
    switch (type) {
      case ArchActionType.Client:
        return collapsedClientIds.includes(id)
      case ArchActionType.Program:
        return isCollapsedProgram(id)
      case ArchActionType.Intake:
        return isCollapsedIntake(id)
      case ArchActionType.Course:
        return isCollapsedCourse(id)
      case ArchActionType.Module:
        return isCollapsedModule(id)
      case ArchActionType.CourseModules:
        return isCollapsedCourseModules(id)
      case ArchActionType.CourseAssessments:
        return isCollapsedCourseAssessments(id)
      case ArchActionType.CourseAssessmentsTests:
        return isCollapsedCourseAssessmentsTests(id)
    }
  }

  const renderActionButtons = (
    type: ArchActionType,
    id: string,
    locked: boolean = false,
    dragHandleProps?: any,
    hasChildren: boolean = true,
  ) => {
    const getActionButtonConfig = (): ActionButtonConfig => {
      switch (type) {
        case ArchActionType.Client:
          return {
            typeNameForAdd: 'program',
            typeName: 'client',
            actionButtons: ['add', ACTION_BUTTON_SPACER, 'collapse'],
          }
        case ArchActionType.Program:
          return {
            typeNameForAdd: 'cohort',
            typeName: 'program',
            actionButtons: ['copy', 'add', 'delete', 'collapse'],
          }
        case ArchActionType.Intake:
          return {
            typeNameForAdd: 'course',
            typeName: 'cohort',
            actionButtons: isEnterprise
              ? ['upload', 'copy', 'add', 'delete', 'collapse']
              : ['upload', 'copy', ACTION_BUTTON_SPACER, 'delete', 'collapse'],
          }
        case ArchActionType.Course:
          return {
            typeNameForAdd: '',
            typeName: 'course',
            actionButtons: ['copy', ACTION_BUTTON_SPACER, 'delete', 'collapse'],
          }
        case ArchActionType.CourseModules:
          return {
            typeNameForAdd: 'module',
            typeName: 'course-modules',
            actionButtons: ['add', ACTION_BUTTON_SPACER, 'collapse'],
          }
        case ArchActionType.CourseAssessments:
          return {
            typeNameForAdd: '',
            typeName: 'course-assessments',
            actionButtons: ['collapse'],
          }
        case ArchActionType.CourseAssessmentsTests:
          return {
            typeNameForAdd: 'quiz',
            typeName: 'course-assessments-tests',
            actionButtons: ['add', ACTION_BUTTON_SPACER, 'collapse'],
          }
        case ArchActionType.Module:
          return {
            typeNameForAdd: 'topic',
            typeName: 'module',
            actionButtons: ['up', 'down', 'copy', 'add', 'delete', 'collapse'],
          }
        case ArchActionType.Topic:
          return {
            typeNameForAdd: '',
            typeName: 'topic',
            actionButtons: [
              'move',
              'up',
              'down',
              'copy',
              ACTION_BUTTON_SPACER,
              'delete',
              ACTION_BUTTON_SPACER,
            ],
          }
        case ArchActionType.Quiz:
          return {
            typeNameForAdd: '',
            typeName: 'quiz',
            actionButtons: [
              'copy',
              ACTION_BUTTON_SPACER,
              'delete',
              ACTION_BUTTON_SPACER,
            ],
          }
      }
    }

    const {
      typeNameForAdd,
      typeName,
      actionButtons: baseActionButtons,
    } = getActionButtonConfig()
    let actionButtons = locked
      ? maskLockedActionButtons(type, baseActionButtons)
      : baseActionButtons

    if (!canCreateDeleteClone) {
      actionButtons = actionButtons.map((actionButton) => {
        if (
          actionButton === 'upload' ||
          actionButton === 'add' ||
          actionButton === 'copy' ||
          actionButton === 'delete'
        ) {
          return ACTION_BUTTON_SPACER
        }

        return actionButton
      })
    }

    const { stroke: iconClassStroke, fill: iconClassFill } = getIconClasses(
      type,
      isEnterprise,
    )

    return (
      <div className="flex flex-row items-center gap-[16px]">
        {actionButtons.map((btn, i) => {
          switch (btn) {
            case 'upload':
              return (
                <div
                  key={i}
                  data-tooltip-id="tooltip-upload-excel"
                  data-testid={`btn-upload-${typeName}-${id}`}
                  onClick={() => onUploadExcel(id, type)}
                  className="arch-action-button"
                  tabIndex={-1}
                >
                  <Svg icon={Icon.Import} className={iconClassStroke} />
                </div>
              )
            case 'add':
              return (
                <div
                  key={i}
                  data-tooltip-id={`tooltip-add-${typeNameForAdd}`}
                  data-testid={`btn-add-${typeNameForAdd}-${id}`}
                  onClick={() => onAdd(id, type)}
                  className="arch-action-button"
                  tabIndex={-1}
                >
                  <Svg icon={Icon.Add} className={iconClassFill} />
                </div>
              )
            case 'copy':
              return (
                <div
                  key={i}
                  data-tooltip-id={`tooltip-copy-${typeName}`}
                  data-testid={`btn-copy-${typeName}-${id}`}
                  onClick={() => onDuplicate(id, type)}
                  className="arch-action-button"
                  tabIndex={-1}
                >
                  <Svg icon={Icon.Copy} className={iconClassStroke} />
                </div>
              )
            case 'up':
              return (
                <div
                  key={i}
                  data-tooltip-id={`tooltip-move-up-${typeName}`}
                  data-testid={`btn-move-up-${typeName}-${id}`}
                  onClick={async () => await onMoveUp(id, type)}
                  className="arch-action-button"
                  tabIndex={-1}
                >
                  <Svg icon={Icon.ArrowUp} className={iconClassFill} />
                </div>
              )
            case 'down':
              return (
                <div
                  key={i}
                  data-tooltip-id={`tooltip-move-down-${typeName}`}
                  data-testid={`btn-move-down-${typeName}-${id}`}
                  onClick={async () => await onMoveDown(id, type)}
                  className="arch-action-button"
                  tabIndex={-1}
                >
                  <Svg icon={Icon.ArrowDown} className={iconClassFill} />
                </div>
              )
            case 'move':
              return dragHandleProps ? (
                <div
                  key={i}
                  {...dragHandleProps}
                  data-tooltip-id={`tooltip-move-${typeName}`}
                  data-testid={`btn-move-${typeName}-${id}`}
                  className="arch-action-button"
                  tabIndex={-1}
                >
                  <Svg icon={Icon.Move} />
                </div>
              ) : null
            case 'delete':
              return (
                <div
                  key={i}
                  data-tooltip-id={`tooltip-delete-${typeName}`}
                  data-testid={`btn-delete-${typeName}-${id}`}
                  onClick={() => onDelete(id, type)}
                  className="arch-action-button"
                  tabIndex={-1}
                >
                  <Svg icon={Icon.Delete} className={iconClassFill} />
                </div>
              )
            case 'collapse':
              return isCollapsed(id, type) && hasChildren ? (
                <div
                  key={i}
                  data-tooltip-id={`tooltip-expand-${typeName}`}
                  data-testid={`btn-expand-${typeName}-${id}`}
                  onClick={() => onToggleCollapse(id, type)}
                  className="arch-action-button"
                  tabIndex={-1}
                >
                  <Svg icon={Icon.Down} className={iconClassFill} />
                </div>
              ) : (
                <div
                  key={i}
                  data-tooltip-id={`tooltip-collapse-${typeName}`}
                  data-testid={`btn-collapse-${typeName}-${id}`}
                  onClick={() =>
                    hasChildren ? onToggleCollapse(id, type) : undefined
                  }
                  className="arch-action-button"
                  tabIndex={-1}
                  style={!hasChildren ? { cursor: 'default' } : undefined}
                >
                  <Svg icon={Icon.Up} className={iconClassFill} />
                </div>
              )
            case ACTION_BUTTON_SPACER:
              return (
                <div
                  key={i}
                  className="arch-action-spacer"
                  aria-hidden="true"
                />
              )
            default:
              return null
          }
        })}
      </div>
    )
  }

  const showTopicCheckbox = () => {
    return checkedActionType() !== ArchActionType.Module
  }

  const resetAllCheckboxes = (checked: boolean) => {
    // reset select all checkbox
    setCheckboxSelectAll(false)
    // reset single delete
    setSingleDelete({ id: null, type: null })

    if (checked) {
      clearAllCheckboxes()
      // reset delete confirmation
      setShowDeleteConfirmation(false)
    } else {
      // clear group checkboxes
      setCheckedCourseModulesIds([])
      setCheckedCourseAssessmentsIds([])
    }
  }

  const isLockLoading = (intakeId: string) => {
    return lockLoading[intakeId] || false
  }

  const onToggleLockedForInake = async (
    intakeId: string,
    locked: boolean,
    intakeName: string,
  ) => {
    // only super admin can lock/unlock
    if (!isSuperAdmin) {
      ToastUtil.warning('Only super admin can lock/unlock')
      return
    }
    if (!selectedClient) {
      ToastUtil.warning('Client not found')
      return
    }
    try {
      setLockLoading({
        ...lockLoading,
        [intakeId]: true,
      })
      await toggleLockedForIntake(intakeId, locked)
      const intakeLabel = getEntityLabel(ArchActionType.Intake, !isEnterprise)
      if (locked) {
        ToastUtil.success(`${intakeLabel} "${intakeName}" locked`)
      } else {
        ToastUtil.success(`${intakeLabel} "${intakeName}" unlocked`)
      }
      // reload program
      await fetchClient(selectedClient.id, true)
    } catch (error) {
      const intakeLabel = getEntityLabel(ArchActionType.Intake, !isEnterprise)
      if (locked) {
        ToastUtil.error(`Failed to lock ${intakeLabel.toLowerCase()}`)
      } else {
        ToastUtil.error(`Failed to unlock ${intakeLabel.toLowerCase()}`)
      }
      console.error(error)
    } finally {
      setLockLoading({
        ...lockLoading,
        [intakeId]: false,
      })
    }
  }

  const renderTopic = (
    topic: ArchTopic,
    moduleId: string,
    moduleIndex: number,
    topicIndex: number,
    locked: boolean,
  ) => {
    const topicNumber = `${moduleIndex + 1}.${topicIndex + 1}`
    const index = ++draggableIndex
    // store to ref
    refDragAndDropDataDict.current[index] = {
      moduleId: moduleId,
      topicId: topic.id,
    }
    return (
      <Draggable key={topic.id} draggableId={topic.id} index={index}>
        {(provided, snapshot) => (
          <li
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={{
              ...provided.draggableProps.style,
              ...getDraggingStyle(snapshot.isDragging),
              borderColor: '#d5d5db',
            }}
            key={`topic-${topic.id}`}
            className="hover:bg-[#F3F3F5]"
          >
            <div
              className="flex w-full flex-row"
              style={{ paddingLeft: nestingPadding(isEnterprise ? 5 : 4) }}
            >
              <div className="flex min-w-0 flex-1 flex-row items-center gap-[12px] pr-[12px]">
                {!locked && showTopicCheckbox() ? (
                  <Checkbox
                    id={`checkbox-topic-${topic.id}`}
                    data-testid={`checkbox-topic-${topic.id}`}
                    checked={checkedTopicIds.includes(topic.id)}
                    onChange={(checked) => {
                      resetAllCheckboxes(checked)
                      setCheckedTopicIds(
                        checked
                          ? [...checkedTopicIds, topic.id]
                          : checkedTopicIds.filter((id) => id !== topic.id),
                      )
                    }}
                    size={CheckboxSize.Small}
                    theme={checkboxTheme}
                  />
                ) : (
                  <div className="w-[27px]" />
                )}
                <div className="arch-topic-list-item-title">{topicNumber}</div>
                <div className="flex min-w-[20%] max-w-[20%] flex-row items-center">
                  {topic.link && (
                    <a
                      className="arch-topic-list-item-link"
                      href={topic.link}
                      target="_blank"
                      rel="noreferrer"
                      onDoubleClick={(e) => {
                        if (locked) return
                        e.preventDefault()
                        Emitter.emit(Events.ShowArchEditTopic, {
                          type: ArchActionType.Topic,
                          topic: topic,
                          position: { x: e.clientX, y: e.clientY },
                        })
                      }}
                    >
                      {topic.name}
                    </a>
                  )}
                  {!topic.link && (
                    <span
                      className="arch-topic-list-item-title"
                      onDoubleClick={(e) => {
                        if (locked) return
                        Emitter.emit(Events.ShowArchEditTopic, {
                          type: ArchActionType.Topic,
                          topic: topic,
                          position: { x: e.clientX, y: e.clientY },
                        })
                      }}
                    >
                      {topic.name}
                    </span>
                  )}
                </div>
                {!locked && (
                  <button
                    data-testid={`btn-edit-topic-${topic.id}`}
                    className="min-w-[17px]"
                    onClick={(e) => {
                      Emitter.emit(Events.ShowArchEditTopic, {
                        type: ArchActionType.Topic,
                        topic: topic,
                        // get the position of the edit button
                        position: {
                          x: e.clientX,
                          y: e.clientY,
                        },
                      })
                    }}
                  >
                    <Svg
                      icon={Icon.Edit}
                      className={IconClass.FillTertiaryTeal}
                    />
                  </button>
                )}
                <span className="arch-topic-list-item-title">
                  {isEnterprise ? 'LO:' : 'Purpose:'}
                </span>
                <TextareaAutosize
                  data-testid={`input-topic-learning-outcome-${topic.id}`}
                  className={`mentem-scrollbar-2 w-full ${
                    readOnlyMode(EditPrefix.TopicLearningOutcome, topic.id)
                      ? 'arch-topic-list-item-title'
                      : 'arch-add-input-small'
                  }`}
                  placeholder="Type learning outcome here"
                  defaultValue={topic.learningOutcome}
                  readOnly={readOnlyMode(
                    EditPrefix.TopicLearningOutcome,
                    topic.id,
                  )}
                  maxRows={2}
                  onDoubleClick={(e) => {
                    if (locked) return
                    onClickOrBlurTextArea(
                      EditPrefix.TopicLearningOutcome,
                      topic.id,
                      true,
                    )
                    focusAndSelectField(e.currentTarget)
                  }}
                  onBlur={() =>
                    onClickOrBlurTextArea(
                      EditPrefix.TopicLearningOutcome,
                      topic.id,
                      false,
                    )
                  }
                  onKeyDown={onKeyDownForTextArea}
                  onKeyUp={(e) => {
                    const timeoutKey = `topic-learning-outcome-${topic.id}`
                    if (e.key === 'Enter') {
                      commitInlineEdit({
                        timeoutKey,
                        nextValue: e.currentTarget.value,
                        previousValue: topic.learningOutcome,
                        emptyWarning: isEnterprise
                          ? 'Learning outcome cannot be empty'
                          : 'Purpose cannot be empty',
                        save: (value, options) =>
                          updateTopicLearningOutcome(
                            topic.id,
                            topic.name,
                            value,
                            options,
                          ),
                        exitEditMode: () =>
                          onClickOrBlurTextArea(
                            EditPrefix.TopicLearningOutcome,
                            topic.id,
                            false,
                          ),
                      })
                    } else {
                      scheduleInlineAutosave({
                        timeoutKey,
                        nextValue: e.currentTarget.value,
                        previousValue: topic.learningOutcome,
                        save: (value, options) =>
                          updateTopicLearningOutcome(
                            topic.id,
                            topic.name,
                            value,
                            options,
                          ),
                      })
                    }
                  }}
                />
                {!locked && (
                  <button
                    data-testid={`btn-edit-learning-outcome-${topic.id}`}
                    className="min-w-[17px]"
                    onClick={() => {
                      // toggle editing mode
                      onClickOrBlurTextArea(
                        EditPrefix.TopicLearningOutcome,
                        topic.id,
                        readOnlyMode(EditPrefix.TopicLearningOutcome, topic.id),
                      )
                    }}
                  >
                    <Svg
                      icon={Icon.Edit}
                      className={IconClass.FillTertiaryTeal}
                    />
                  </button>
                )}
              </div>
              {!isEnterprise && (
                <div className="flex flex-shrink-0 items-center gap-1">
                  <span className="arch-topic-list-item-title">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <path
                        d="M9 15.375C12.5208 15.375 15.375 12.5208 15.375 9C15.375 5.47918 12.5208 2.625 9 2.625C5.47918 2.625 2.625 5.47918 2.625 9C2.625 12.5208 5.47918 15.375 9 15.375Z"
                        stroke="black"
                      />
                      <path
                        d="M12.375 9H9.1875C9.13777 9 9.09008 8.98025 9.05492 8.94508C9.01975 8.90992 9 8.86223 9 8.8125V6.375"
                        stroke="black"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  <input
                    data-testid={`input-topic-duration-${topic.id}`}
                    className={`w-[44px] ${
                      readOnlyMode(EditPrefix.TopicDuration, topic.id)
                        ? 'arch-topic-list-item-title'
                        : 'arch-add-input-small'
                    }`}
                    defaultValue={
                      topic.durationSeconds != null
                        ? Math.round(topic.durationSeconds / 60)
                        : ''
                    }
                    placeholder="minutes"
                    readOnly={readOnlyMode(EditPrefix.TopicDuration, topic.id)}
                    onDoubleClick={(e) => {
                      if (locked) return
                      onClickOrBlurTextArea(
                        EditPrefix.TopicDuration,
                        topic.id,
                        true,
                      )
                      focusAndSelectField(e.currentTarget)
                    }}
                    onBlur={(e) => {
                      onClickOrBlurTextArea(
                        EditPrefix.TopicDuration,
                        topic.id,
                        false,
                      )
                      const value = e.currentTarget.value.trim()
                      if (
                        value !==
                        String(
                          topic.durationSeconds != null
                            ? Math.round(topic.durationSeconds / 60)
                            : '',
                        )
                      ) {
                        updateTopicDuration(topic.id, value)
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur()
                      }
                    }}
                  />
                  {!locked && (
                    <button
                      data-testid={`btn-edit-duration-${topic.id}`}
                      className="min-w-[17px]"
                      onClick={() => {
                        onClickOrBlurTextArea(
                          EditPrefix.TopicDuration,
                          topic.id,
                          readOnlyMode(EditPrefix.TopicDuration, topic.id),
                        )
                      }}
                    >
                      <Svg
                        icon={Icon.Edit}
                        className={IconClass.FillTertiaryTeal}
                      />
                    </button>
                  )}
                </div>
              )}
              <div className="flex-shrink-0 pl-3">
                {renderActionButtons(
                  ArchActionType.Topic,
                  topic.id,
                  locked,
                  provided.dragHandleProps,
                )}
              </div>
            </div>
          </li>
        )}
      </Draggable>
    )
  }

  const renderModule = (
    module: ArchModule,
    moduleIndex: number,
    locked: boolean,
  ) => {
    const index = ++draggableIndex
    // console.log(`module-${module.id} index: ${index}`)
    // store to ref
    refDragAndDropDataDict.current[index] = {
      moduleId: module.id,
      topicId: undefined,
    }
    return (
      <React.Fragment key={`module-${moduleIndex}`}>
        <Draggable
          key={module.id}
          draggableId={module.id}
          index={index}
          isDragDisabled={true}
        >
          {(provided, snapshot) => (
            <li
              ref={provided.innerRef}
              {...provided.draggableProps}
              style={{
                ...provided.draggableProps.style,
                boxShadow: snapshot.isDragging
                  ? '0px 16px 24px 0px rgba(13, 13, 13, 0.2)'
                  : '',
                ...unswoModuleStyle,
              }}
              key={`module-${module.id}`}
              className="arch-module-list-item flex flex-row items-center"
            >
              <div>
                <div
                  className="flex w-full flex-row items-center gap-[6px]"
                  style={{ paddingLeft: nestingPadding(isEnterprise ? 4 : 3) }}
                >
                  {renderCheckboxOrPlaceholder(
                    locked,
                    <Checkbox
                      id={`checkbox-module-${module.id}`}
                      data-testid={`checkbox-module-${module.id}`}
                      checked={checkedModuleIds.includes(module.id)}
                      onChange={(checked) => {
                        resetAllCheckboxes(checked)
                        setCheckedModuleIds(
                          checked
                            ? [...checkedModuleIds, module.id]
                            : checkedModuleIds.filter((id) => id !== module.id),
                        )
                      }}
                      size={CheckboxSize.Small}
                      theme={checkboxThemeOnHeader}
                    />,
                  )}
                  <span className="whitespace-nowrap">
                    {getEntityLabel(ArchActionType.Module, !isEnterprise)}{' '}
                    {moduleIndex + 1}:{' '}
                  </span>
                  {/* <span>{module.name}</span> */}
                  <TextareaAutosize
                    data-testid={`input-module-name-${module.id}`}
                    ref={(el: HTMLTextAreaElement | null) => {
                      if (el)
                        refEditTextareas.current.set(`module-${module.id}`, el)
                      else
                        refEditTextareas.current.delete(`module-${module.id}`)
                    }}
                    className={`w-full ${
                      readOnlyMode(EditPrefix.ModuleName, module.id)
                        ? 'arch-module-list-item-read-only'
                        : 'arch-module-list-item-add-input'
                    }`}
                    placeholder="Type module name here"
                    defaultValue={module.name}
                    readOnly={readOnlyMode(EditPrefix.ModuleName, module.id)}
                    onDoubleClick={() =>
                      onClickOrBlurTextArea(
                        EditPrefix.ModuleName,
                        module.id,
                        true,
                      )
                    }
                    onBlur={() =>
                      onClickOrBlurTextArea(
                        EditPrefix.ModuleName,
                        module.id,
                        false,
                      )
                    }
                    onKeyDown={onKeyDownForTextArea}
                    onKeyUp={(e) => {
                      const timeoutKey = `module-name-${module.id}`
                      if (e.key === 'Enter') {
                        commitInlineEdit({
                          timeoutKey,
                          nextValue: e.currentTarget.value,
                          previousValue: module.name,
                          emptyWarning: 'Module name cannot be empty',
                          save: (value, options) =>
                            updateModuleName(module.id, value, options),
                          exitEditMode: () =>
                            onClickOrBlurTextArea(
                              EditPrefix.ModuleName,
                              module.id,
                              false,
                            ),
                        })
                      } else {
                        scheduleInlineAutosave({
                          timeoutKey,
                          nextValue: e.currentTarget.value,
                          previousValue: module.name,
                          save: (value, options) =>
                            updateModuleName(module.id, value, options),
                        })
                      }
                    }}
                  />
                  {!readOnlyMode(EditPrefix.ModuleName, module.id) && (
                    <InlineEditButtons
                      onConfirm={() => {
                        commitInlineEdit({
                          timeoutKey: `module-name-${module.id}`,
                          nextValue:
                            refEditTextareas.current.get(`module-${module.id}`)
                              ?.value || '',
                          previousValue: module.name,
                          emptyWarning: 'Module name cannot be empty',
                          save: (value, options) =>
                            updateModuleName(module.id, value, options),
                          exitEditMode: () =>
                            onClickOrBlurTextArea(
                              EditPrefix.ModuleName,
                              module.id,
                              false,
                            ),
                        })
                      }}
                      onCancel={() => {
                        cancelInlineEdit({
                          timeoutKey: `module-name-${module.id}`,
                          textareaKey: `module-${module.id}`,
                          previousValue: module.name,
                          exitEditMode: () =>
                            onClickOrBlurTextArea(
                              EditPrefix.ModuleName,
                              module.id,
                              false,
                            ),
                        })
                      }}
                    />
                  )}
                </div>
                <div className="flex flex-row items-center gap-[12px]">
                  {renderActionButtons(
                    ArchActionType.Module,
                    module.id,
                    locked,
                    undefined,
                    module.topics.length > 0,
                  )}
                </div>
              </div>
            </li>
          )}
        </Draggable>

        {module.topics.length > 0 &&
          !isCollapsed(module.id, ArchActionType.Module) &&
          module.topics.map((topic, topicIndex) =>
            renderTopic(topic, module.id, moduleIndex, topicIndex, locked),
          )}

        {showAddTopicIds.includes(module.id) &&
          !isCollapsed(module.id, ArchActionType.Module) && (
            <li key="add-topic" className="arch-topic-list-item">
              <AddEntityInput
                entityName="Topic"
                label={`${moduleIndex + 1}.${module.topics.length + 1}:`}
                wrapperStyle={{
                  paddingLeft: nestingPadding(isEnterprise ? 5 : 4, 12),
                }}
                isAdding={isAddingTopicIds.includes(module.id)}
                onConfirm={(name) => createTopic(module.id, name)}
                onCancel={() =>
                  setShowAddTopicIds(
                    showAddTopicIds.filter((id) => id !== module.id),
                  )
                }
                onKeyDown={onKeyDownForTextArea}
                testId={`input-add-topic-${module.id}`}
                inputClassName="arch-add-topic-input"
              />
            </li>
          )}
      </React.Fragment>
    )
  }

  const renderQuiz = (
    quiz: Quiz,
    courseId: string,
    quizIndex: number,
    locked: boolean,
  ) => {
    return (
      <Draggable
        key={`quiz-${courseId}-${quiz.id}-${quizIndex}`}
        draggableId={`quiz-${courseId}-${quiz.id}-${quizIndex}`}
        index={++draggableIndex}
        isDragDisabled={true}
      >
        {(provided, snapshot) => (
          <li
            ref={provided.innerRef}
            {...provided.draggableProps}
            key={`quiz-${courseId}-${quiz.id}-${quizIndex}`}
            style={{
              ...provided.draggableProps.style,
              boxShadow: snapshot.isDragging
                ? '0px 16px 24px 0px rgba(13, 13, 13, 0.2)'
                : '',
              borderLeft: '1px solid #D9D9D9C2',
              borderRight: '1px solid #D9D9D9C2',
            }}
          >
            <div style={{ paddingLeft: nestingPadding(isEnterprise ? 5 : 4) }}>
              {renderCheckboxOrPlaceholder(
                locked,
                <Checkbox
                  id={`checkbox-quiz-${quiz.id}`}
                  data-testid={`checkbox-quiz-${quiz.id}`}
                  checked={checkedQuizIds.includes(quiz.id)}
                  onChange={(checked) => {
                    resetAllCheckboxes(checked)
                    setCheckedQuizIds(
                      checked
                        ? [...checkedQuizIds, quiz.id]
                        : checkedQuizIds.filter((id) => id !== quiz.id),
                    )
                  }}
                  size={CheckboxSize.Small}
                  theme={checkboxTheme}
                />,
              )}
              <div className="w-[70%] pl-[6px]">{quiz.name}</div>
              <div className="arch-topic-last-updated-at">
                <span>Last updated:</span>
                <span>{getDateTimeString(quiz.updatedAt)}</span>
              </div>
              <QuizStatusText quiz={quiz} />
              <div className="flex w-[30%] justify-end">
                {renderActionButtons(ArchActionType.Quiz, quiz.id, locked)}
              </div>
            </div>
          </li>
        )}
      </Draggable>
    )
  }

  const renderCourse = (
    course: ArchCourse,
    courseIndex: number,
    locked: boolean,
  ) => {
    return (
      <React.Fragment key={`course-${course.id}`}>
        <Draggable
          key={`course-${course.id}`}
          draggableId={`course-${course.id}`}
          index={++draggableIndex}
          isDragDisabled={true}
        >
          {(provided, snapshot) => (
            <li
              ref={provided.innerRef}
              className="hover:bg-[#F3F3F5]"
              {...provided.draggableProps}
              key={`course-${course.id}`}
              style={{
                ...provided.draggableProps.style,
                display: isEnterprise ? undefined : 'none',
                boxShadow: snapshot.isDragging
                  ? '0px 16px 24px 0px rgba(13, 13, 13, 0.2)'
                  : '',
                borderLeft: '1px solid #D9D9D9C2',
                borderRight: '1px solid #D9D9D9C2',
              }}
            >
              <div>
                <div
                  className="flex w-full flex-row items-center gap-2"
                  style={{ paddingLeft: nestingPadding(2) }}
                >
                  {renderCheckboxOrPlaceholder(
                    locked,
                    <Checkbox
                      id={`checkbox-course-${course.id}`}
                      data-testid={`checkbox-course-${course.id}`}
                      checked={checkedCourseIds.includes(course.id)}
                      onChange={(checked) => {
                        resetAllCheckboxes(checked)
                        setCheckedCourseIds(
                          checked
                            ? [...checkedCourseIds, course.id]
                            : checkedCourseIds.filter((id) => id !== course.id),
                        )
                      }}
                      size={CheckboxSize.Small}
                      theme={checkboxTheme}
                    />,
                  )}
                  <span className="whitespace-nowrap">
                    Course {courseIndex + 1}:
                  </span>{' '}
                  <TextareaAutosize
                    data-testid={`input-course-name-${course.id}`}
                    ref={(el: HTMLTextAreaElement | null) => {
                      if (el)
                        refEditTextareas.current.set(`course-${course.id}`, el)
                      else
                        refEditTextareas.current.delete(`course-${course.id}`)
                    }}
                    className={`w-full ${
                      readOnlyMode(EditPrefix.CourseName, course.id)
                        ? 'arch-read-only'
                        : 'arch-add-input'
                    }`}
                    placeholder="Type course name here"
                    defaultValue={course.name}
                    readOnly={readOnlyMode(EditPrefix.CourseName, course.id)}
                    onDoubleClick={() => {
                      if (locked) return
                      onClickOrBlurTextArea(
                        EditPrefix.CourseName,
                        course.id,
                        true,
                      )
                    }}
                    onBlur={() =>
                      onClickOrBlurTextArea(
                        EditPrefix.CourseName,
                        course.id,
                        false,
                      )
                    }
                    onKeyDown={onKeyDownForTextArea}
                    onKeyUp={(e) => {
                      const timeoutKey = `course-name-${course.id}`
                      if (e.key === 'Enter') {
                        commitInlineEdit({
                          timeoutKey,
                          nextValue: e.currentTarget.value,
                          previousValue: course.name,
                          emptyWarning: 'Course name cannot be empty',
                          save: (value, options) =>
                            updateCourseName(course.id, value, options),
                          exitEditMode: () =>
                            onClickOrBlurTextArea(
                              EditPrefix.CourseName,
                              course.id,
                              false,
                            ),
                        })
                      } else {
                        scheduleInlineAutosave({
                          timeoutKey,
                          nextValue: e.currentTarget.value,
                          previousValue: course.name,
                          save: (value, options) =>
                            updateCourseName(course.id, value, options),
                        })
                      }
                    }}
                  />
                  {!readOnlyMode(EditPrefix.CourseName, course.id) && (
                    <InlineEditButtons
                      onConfirm={() => {
                        commitInlineEdit({
                          timeoutKey: `course-name-${course.id}`,
                          nextValue:
                            refEditTextareas.current.get(`course-${course.id}`)
                              ?.value || '',
                          previousValue: course.name,
                          emptyWarning: 'Course name cannot be empty',
                          save: (value, options) =>
                            updateCourseName(course.id, value, options),
                          exitEditMode: () =>
                            onClickOrBlurTextArea(
                              EditPrefix.CourseName,
                              course.id,
                              false,
                            ),
                        })
                      }}
                      onCancel={() => {
                        cancelInlineEdit({
                          timeoutKey: `course-name-${course.id}`,
                          textareaKey: `course-${course.id}`,
                          previousValue: course.name,
                          exitEditMode: () =>
                            onClickOrBlurTextArea(
                              EditPrefix.CourseName,
                              course.id,
                              false,
                            ),
                        })
                      }}
                    />
                  )}
                </div>
                <div className="flex justify-end">
                  {renderActionButtons(
                    ArchActionType.Course,
                    course.id,
                    locked,
                  )}
                </div>
              </div>
            </li>
          )}
        </Draggable>

        {isCourseChildHeaderVisible(collapsedCourseIds, course.id) && (
          <Draggable
            key={`modules-${course.id}`}
            draggableId={`modules-${course.id}`}
            index={++draggableIndex}
          >
            {(provided, snapshot) => (
              <li
                ref={provided.innerRef}
                {...provided.draggableProps}
                key={`modules-${course.id}`}
                className="arch-editor-list-title arch-header-list-item"
                style={{
                  ...provided.draggableProps.style,
                  ...getDraggingStyle(snapshot.isDragging),
                  ...unswoHeaderStyle,
                }}
              >
                <div
                  style={{ paddingLeft: nestingPadding(isEnterprise ? 3 : 2) }}
                >
                  {renderCheckboxOrPlaceholder(
                    locked,
                    <Checkbox
                      id={`checkbox-modules-${course.id}`}
                      data-testid={`checkbox-modules-${course.id}`}
                      checked={checkedCourseModulesIds.includes(course.id)}
                      onChange={(checked) => {
                        resetAllCheckboxes(checked)
                        // check all modules in the same course
                        const moduleIds = course.modules.map(
                          (module) => module.id,
                        )
                        setCheckedModuleIds(
                          checked
                            ? [...checkedModuleIds, ...moduleIds]
                            : checkedModuleIds.filter(
                                (id) => !moduleIds.includes(id),
                              ),
                        )
                        // set checked modules ids
                        setCheckedCourseModulesIds(
                          checked
                            ? [...checkedCourseModulesIds, course.id]
                            : checkedCourseModulesIds.filter(
                                (id) => id !== course.id,
                              ),
                        )
                      }}
                      size={CheckboxSize.Small}
                      theme={checkboxThemeOnHeader}
                    />,
                  )}
                  <div className="w-[70%] pl-[6px]">{`${getEntityLabel(
                    ArchActionType.Module,
                    !isEnterprise,
                  )}(s):`}</div>
                  <div className="flex w-[30%] justify-end">
                    {renderActionButtons(
                      ArchActionType.CourseModules,
                      course.id,
                      locked,
                      undefined,
                      course.modules.length > 0,
                    )}
                  </div>
                </div>
              </li>
            )}
          </Draggable>
        )}

        {course.modules &&
          isCourseModuleItemsVisible(
            collapsedCourseIds,
            collapsedCourseModulesIds,
            course.id,
          ) &&
          course.modules.map((module, index) =>
            renderModule(module, index, locked),
          )}

        {showAddModuleIds.includes(course.id) &&
          isCourseModuleItemsVisible(
            collapsedCourseIds,
            collapsedCourseModulesIds,
            course.id,
          ) && (
            <li
              key="add-module"
              className="arch-module-list-item"
              style={{ ...unswoModuleStyle }}
            >
              <AddEntityInput
                entityName={getEntityLabel(
                  ArchActionType.Module,
                  !isEnterprise,
                )}
                label={`${getEntityLabel(
                  ArchActionType.Module,
                  !isEnterprise,
                )} ${course.modules.length + 1}:`}
                isAdding={isAddingModuleIds.includes(course.id)}
                onConfirm={(name) => createModule(course.id, name)}
                onCancel={() =>
                  setShowAddModuleIds(
                    showAddModuleIds.filter((id) => id !== course.id),
                  )
                }
                onKeyDown={onKeyDownForTextArea}
                testId={`input-add-module-${course.id}`}
                inputClassName="arch-add-module-input"
                wrapperStyle={{
                  paddingLeft: nestingPadding(isEnterprise ? 4 : 3, 6),
                }}
              />
            </li>
          )}

        {isCourseChildHeaderVisible(collapsedCourseIds, course.id) && (
          <Draggable
            key={`assessments-${course.id}`}
            draggableId={`assessments-${course.id}`}
            index={++draggableIndex}
            isDragDisabled={true}
          >
            {(provided, snapshot) => (
              <li
                ref={provided.innerRef}
                {...provided.draggableProps}
                key={`assessments-${course.id}`}
                className="arch-editor-list-title arch-header-list-item"
                style={{
                  ...provided.draggableProps.style,
                  ...getDraggingStyle(snapshot.isDragging),
                  ...unswoHeaderStyle,
                }}
              >
                <div
                  style={{ paddingLeft: nestingPadding(isEnterprise ? 3 : 2) }}
                >
                  {renderCheckboxOrPlaceholder(
                    locked,
                    <Checkbox
                      id={`checkbox-assesments-${course.id}`}
                      data-testid={`checkbox-assessments-${course.id}`}
                      checked={checkedCourseAssessmentsIds.includes(course.id)}
                      onChange={(checked) => {
                        resetAllCheckboxes(checked)
                        // check all quizzes in the same course
                        const quizIds = course.quizzes.map((quiz) => quiz.id)
                        setCheckedQuizIds(
                          checked
                            ? [...checkedQuizIds, ...quizIds]
                            : checkedQuizIds.filter(
                                (id) => !quizIds.includes(id),
                              ),
                        )

                        // set checked course assessments ids
                        setCheckedCourseAssessmentsIds(
                          checked
                            ? [...checkedCourseAssessmentsIds, course.id]
                            : checkedCourseAssessmentsIds.filter(
                                (id) => id !== course.id,
                              ),
                        )
                      }}
                      size={CheckboxSize.Small}
                      theme={checkboxThemeOnHeader}
                    />,
                  )}
                  <div className="w-[70%] pl-[6px]">Assessment(s):</div>
                  <div className="flex w-[30%] justify-end">
                    {renderActionButtons(
                      ArchActionType.CourseAssessments,
                      course.id,
                      locked,
                    )}
                  </div>
                </div>
              </li>
            )}
          </Draggable>
        )}

        {isCourseAssessmentItemsVisible(
          collapsedCourseIds,
          collapsedCourseAssessmentsIds,
          course.id,
        ) && (
          <Draggable
            key={`tests-${course.id}`}
            draggableId={`tests-${course.id}`}
            index={++draggableIndex}
            isDragDisabled={true}
          >
            {(provided, snapshot) => (
              <li
                ref={provided.innerRef}
                {...provided.draggableProps}
                key={`tests-${course.id}`}
                className="arch-module-list-item flex flex-row items-center"
                style={{
                  ...provided.draggableProps.style,
                  ...getDraggingStyle(snapshot.isDragging),
                  ...unswoHeaderStyle,
                }}
              >
                <div
                  className="flex w-full flex-row items-center gap-[6px]"
                  style={{ paddingLeft: nestingPadding(isEnterprise ? 4 : 3) }}
                >
                  {renderCheckboxOrPlaceholder(
                    locked,
                    <Checkbox
                      id={`checkbox-tests-${course.id}`}
                      data-testid={`checkbox-tests-${course.id}`}
                      checked={checkedCourseAssessmentsIds.includes(course.id)}
                      onChange={(checked) => {
                        resetAllCheckboxes(checked)
                        // check all quizzes in the same course
                        const quizIds = course.quizzes.map((quiz) => quiz.id)
                        setCheckedQuizIds(
                          checked
                            ? [...checkedQuizIds, ...quizIds]
                            : checkedQuizIds.filter(
                                (id) => !quizIds.includes(id),
                              ),
                        )

                        // set checked course assessments ids
                        setCheckedCourseAssessmentsIds(
                          checked
                            ? [...checkedCourseAssessmentsIds, course.id]
                            : checkedCourseAssessmentsIds.filter(
                                (id) => id !== course.id,
                              ),
                        )
                      }}
                      size={CheckboxSize.Small}
                      theme={checkboxThemeOnHeader}
                    />,
                  )}
                  <span className="whitespace-nowrap">Test(s):</span>
                  <div className="grow" />
                  <div className="flex flex-row items-center gap-[12px]">
                    {renderActionButtons(
                      ArchActionType.CourseAssessmentsTests,
                      course.id,
                      locked,
                      undefined,
                      course.quizzes.length > 0,
                    )}
                  </div>
                </div>
              </li>
            )}
          </Draggable>
        )}

        {course.quizzes &&
          isCourseAssessmentItemsVisible(
            collapsedCourseIds,
            collapsedCourseAssessmentsIds,
            course.id,
          ) &&
          !isCollapsedCourseAssessmentsTests(course.id) &&
          course.quizzes.map((quiz, index) =>
            renderQuiz(quiz, course.id, index, locked),
          )}
      </React.Fragment>
    )
  }

  const buildIntakeRow = (intake: ArchIntake, intakeIndex: number) => {
    const isReadOnly = readOnlyMode(EditPrefix.IntakeName, intake.id)
    const isAutoFocus = canAutoFocus(EditPrefix.IntakeName, intake.id)

    const coursesJsx = !isCollapsedIntake(intake.id) ? (
      <>
        {intake.courses.map((course, index) =>
          renderCourse(course, index, intake.locked),
        )}
        {showAddCourseIds.includes(intake.id) && (
          <li key="add-course" className="arch-course-list-item">
            <AddEntityInput
              entityName="Course"
              label={`Course ${intake.courses.length + 1}:`}
              isAdding={isAddingCourseIds.includes(intake.id)}
              onConfirm={(name) => createCourse(intake.id, name)}
              onCancel={() =>
                setShowAddCourseIds(
                  showAddCourseIds.filter((id) => id !== intake.id),
                )
              }
              onKeyDown={onKeyDownForTextArea}
              testId={`input-add-course-${intake.id}`}
              inputClassName="arch-add-course-input"
              wrapperStyle={{ paddingLeft: nestingPadding(1, 6) }}
            />
          </li>
        )}
      </>
    ) : null

    return (
      <IntakeRow
        key={intake.id}
        intake={intake}
        isEnterprise={isEnterprise}
        isSuperAdmin={isSuperAdmin}
        unswoHeaderStyle={unswoHeaderStyle}
        isLockLoading={isLockLoading(intake.id)}
        isReadOnly={isReadOnly}
        isAutoFocus={isAutoFocus}
        refEditTextareas={refEditTextareas}
        actionButtons={renderActionButtons(
          ArchActionType.Intake,
          intake.id,
          intake.locked,
          undefined,
          intake.courses.length > 0,
        )}
        onToggleLocked={() =>
          onToggleLockedForInake(intake.id, !intake.locked, intake.name)
        }
        onDoubleClick={() => {
          if (!intake.locked)
            onClickOrBlurTextArea(EditPrefix.IntakeName, intake.id, true)
        }}
        onBlur={() =>
          onClickOrBlurTextArea(EditPrefix.IntakeName, intake.id, false)
        }
        onKeyDown={onKeyDownForTextArea}
        onKeyUp={(e) => {
          const timeoutKey = `intake-name-${intake.id}`
          if (e.key === 'Enter') {
            commitInlineEdit({
              timeoutKey,
              nextValue: e.currentTarget.value,
              previousValue: intake.name,
              emptyWarning: 'Intake name cannot be empty',
              save: (value, options) =>
                updateIntakeName(intake.id, value, options),
              exitEditMode: () =>
                onClickOrBlurTextArea(EditPrefix.IntakeName, intake.id, false),
            })
          } else {
            scheduleInlineAutosave({
              timeoutKey,
              nextValue: e.currentTarget.value,
              previousValue: intake.name,
              save: (value, options) =>
                updateIntakeName(intake.id, value, options),
            })
          }
        }}
        onConfirmEdit={() => {
          commitInlineEdit({
            timeoutKey: `intake-name-${intake.id}`,
            nextValue:
              refEditTextareas.current.get(`intake-${intake.id}`)?.value || '',
            previousValue: intake.name,
            emptyWarning: 'Intake name cannot be empty',
            save: (value, options) =>
              updateIntakeName(intake.id, value, options),
            exitEditMode: () =>
              onClickOrBlurTextArea(EditPrefix.IntakeName, intake.id, false),
          })
        }}
        onCancelEdit={() => {
          cancelInlineEdit({
            timeoutKey: `intake-name-${intake.id}`,
            textareaKey: `intake-${intake.id}`,
            previousValue: intake.name,
            exitEditMode: () =>
              onClickOrBlurTextArea(EditPrefix.IntakeName, intake.id, false),
          })
        }}
      >
        {coursesJsx}
      </IntakeRow>
    )
  }

  const buildProgramRow = (program: ArchProgram, programIndex: number) => {
    draggableIndex = programIndex * 10000
    const thisDraggableIndex = ++draggableIndex
    const isReadOnly = readOnlyMode(EditPrefix.ProgramName, program.id)
    const isAutoFocus = canAutoFocus(EditPrefix.ProgramName, program.id)
    const isAddingIntake = isAddingIntakeIds.includes(program.id)

    const intakesToShow =
      filteredIntakeName === undefined
        ? program.intakes
        : program.intakes.filter((i) => i.name === filteredIntakeName)

    const intakesJsx = !isCollapsedProgram(program.id) ? (
      <>{intakesToShow.map((intake, index) => buildIntakeRow(intake, index))}</>
    ) : null

    return (
      <ProgramRow
        key={program.id}
        program={program}
        draggableIndex={thisDraggableIndex}
        style={unswoHeaderStyle}
        isReadOnly={isReadOnly}
        isAutoFocus={isAutoFocus}
        refEditTextareas={refEditTextareas}
        actionButtons={renderActionButtons(
          ArchActionType.Program,
          program.id,
          false,
          undefined,
          program.intakes.length > 0,
        )}
        onDoubleClick={() =>
          onClickOrBlurTextArea(EditPrefix.ProgramName, program.id, true)
        }
        onBlur={() =>
          onClickOrBlurTextArea(EditPrefix.ProgramName, program.id, false)
        }
        onKeyDown={onKeyDownForTextArea}
        onKeyUp={(e) => {
          const timeoutKey = `program-name-${program.id}`
          if (e.key === 'Enter') {
            commitInlineEdit({
              timeoutKey,
              nextValue: e.currentTarget.value,
              previousValue: program.name,
              emptyWarning: 'Program name cannot be empty',
              save: (value, options) =>
                updateProgramName(program.id, value, options),
              exitEditMode: () =>
                onClickOrBlurTextArea(
                  EditPrefix.ProgramName,
                  program.id,
                  false,
                ),
            })
          } else {
            scheduleInlineAutosave({
              timeoutKey,
              nextValue: e.currentTarget.value,
              previousValue: program.name,
              save: (value, options) =>
                updateProgramName(program.id, value, options),
            })
          }
        }}
        onConfirmEdit={() => {
          commitInlineEdit({
            timeoutKey: `program-name-${program.id}`,
            nextValue:
              refEditTextareas.current.get(`program-${program.id}`)?.value ||
              '',
            previousValue: program.name,
            emptyWarning: 'Program name cannot be empty',
            save: (value, options) =>
              updateProgramName(program.id, value, options),
            exitEditMode: () =>
              onClickOrBlurTextArea(EditPrefix.ProgramName, program.id, false),
          })
        }}
        onCancelEdit={() => {
          cancelInlineEdit({
            timeoutKey: `program-name-${program.id}`,
            textareaKey: `program-${program.id}`,
            previousValue: program.name,
            exitEditMode: () =>
              onClickOrBlurTextArea(EditPrefix.ProgramName, program.id, false),
          })
        }}
        onAfterRender={updateDragAndDropDataDict}
      >
        {intakesJsx}
        {showAddIntakeIds.includes(program.id) && (
          <li key="add-intake" className="arch-intake-list-item">
            <AddEntityInput
              entityName={getEntityLabel(ArchActionType.Intake, !isEnterprise)}
              wrapperStyle={{ paddingLeft: nestingPadding(1) }}
              isAdding={isAddingIntake}
              onConfirm={(name) => createIntake(program.id, name)}
              onCancel={() =>
                setShowAddIntakeIds(
                  showAddIntakeIds.filter((id) => id !== program.id),
                )
              }
              onKeyDown={onKeyDownForTextArea}
              testId={`input-add-intake-${program.id}`}
              inputClassName="arch-add-intake-input"
            />
          </li>
        )}
      </ProgramRow>
    )
  }

  const updateTopicPosition = async (moduleId: string, positions: string[]) => {
    if (!selectedClient) {
      return
    }
    try {
      await updateArchModule(moduleId, undefined, positions)
    } catch (error) {
      ToastUtil.error('Failed to update position')
      console.error(error)
    }
  }

  const moveTopicToModule = async (
    topicId: string,
    destinationModuleId: string,
  ) => {
    if (!selectedClient) {
      return
    }
    try {
      await moveArchTopic(topicId, destinationModuleId)
    } catch (error) {
      ToastUtil.error('Failed to move topic')
      console.error(error)
    }
  }

  const reorderTopics = (
    topicIds: string[],
    sourceId: string,
    targetId?: string,
  ) => {
    console.log(`sourceId`, sourceId)
    console.log(`targetId`, targetId)
    console.log(`before ids`, topicIds)

    if (!targetId) {
      // add it to the end
      topicIds.push(sourceId)
      return topicIds
    }

    const sourceIndex = topicIds.indexOf(sourceId)
    const targetIndex = topicIds.indexOf(targetId)

    if (sourceIndex === -1 || targetIndex === -1) {
      console.log('Source or target index not found')
      return topicIds
    }

    const [removed] = topicIds.splice(sourceIndex, 1)
    console.log(`removed`, removed)
    if (removed) {
      topicIds.splice(targetIndex, 0, removed)
    }
    console.log(`after ids`, topicIds)
    // filter null and undefined values
    topicIds.filter((id) => id !== null && id !== undefined && id !== '')
    console.log(`filter ids`, topicIds)
    return topicIds
  }

  // DEBUG USE
  // const onDragEnd = async (result: any) => {
  //   console.log('Drag end', result)
  // }

  const onDragEnd = async (result: any) => {
    console.log('Drag end', result)
    if (!result.destination) {
      return
    }
    if (!selectedClient) {
      return
    }
    // update the order
    const { source, destination } = result
    // find the dragged item - topic by index from ref
    const sourceData = refDragAndDropDataDict.current[source.index]
    const destinationData = refDragAndDropDataDict.current[destination.index]
    console.log(`sourceData`, sourceData)
    console.log(`destinationData`, destinationData)
    if (!sourceData || !destinationData) {
      console.log('No source or destination data')
      return
    }

    const sourceTopicId = sourceData.topicId
    const destinationTopicId = destinationData.topicId // undefined if moving to another module
    if (!sourceTopicId) {
      console.log('No source topic id')
      return
    }

    // no same index
    if (source.index === destination.index) {
      console.log('Same index')
      return
    }

    const sourceModuleId = sourceData.moduleId
    let destinationModuleId = destinationData.moduleId
    if (
      destinationData.topicId === undefined &&
      source.index > destination.index &&
      destinationData.previousModuleId
    ) {
      destinationModuleId = destinationData.previousModuleId
    } else if (
      destinationData.topicId === undefined &&
      source.index < destination.index &&
      destinationData.nextModuleId
    ) {
      destinationModuleId = destinationData.nextModuleId
    }

    // check if moving topic to another module or reordering topics in the same module
    if (sourceModuleId === destinationModuleId) {
      console.log('Reorder topics in the same module')
      // reorder topics in the same module, get the new ids order
      // current topic ids order in the module
      const module = selectedClient.programs
        ?.map((program) =>
          program.intakes?.map((intake) =>
            intake.courses?.map((course) =>
              course.modules?.find((module) => module.id === sourceModuleId),
            ),
          ),
        )
        .flat(3)
        .filter(Boolean)[0]
      if (!module) {
        return
      }
      console.log(`module`, module)
      const topicIds = module.topics?.map((topic) => topic.id)
      console.log(`source`, source)
      console.log(`destination`, destination)
      console.log(`before reordering topics`, topicIds)
      // reorder the topic ids
      const newTopicIds = reorderTopics(
        topicIds,
        sourceTopicId,
        destinationTopicId,
      )
      console.log(`new topic ids`, newTopicIds)

      // update local state
      // find the source module
      const sourceModule = selectedClient.programs
        ?.map((program) =>
          program.intakes?.map((intake) =>
            intake.courses?.map((course) =>
              course.modules?.find(
                (module) => module.id === sourceData.moduleId,
              ),
            ),
          ),
        )
        .flat(3)
        .filter(Boolean)[0]
      if (!sourceModule) {
        return
      }
      // reorder the topics by newTopicIds
      // console.log('New topic ids', newTopicIds)
      // console.log('Before reordering topics', sourceModule.topics)
      sourceModule.topics.sort(
        (a, b) => newTopicIds.indexOf(a.id) - newTopicIds.indexOf(b.id),
      )
      // console.log('Reordered topics', sourceModule.topics)

      // update the order in database
      await updateTopicPosition(sourceData.moduleId, newTopicIds)
    } else {
      // move topic to another module
      // console.log('Move topic to another module')
      if (!sourceData.topicId || !destinationData.moduleId) {
        console.log('No source topic id or destination module id')
        return
      }

      // update local state
      // find the source module
      const sourceModule = selectedClient.programs
        ?.map((program) =>
          program.intakes?.map((intake) =>
            intake.courses?.map((course) =>
              course.modules?.find((module) => module.id === sourceModuleId),
            ),
          ),
        )
        .flat(3)
        .filter(Boolean)[0]
      if (!sourceModule) {
        return
      }
      // find the destination module
      const destinationModule = selectedClient.programs
        ?.map((program) =>
          program.intakes?.map((intake) =>
            intake.courses?.map((course) =>
              course.modules?.find(
                (module) => module.id === destinationModuleId,
              ),
            ),
          ),
        )
        .flat(3)
        .filter(Boolean)[0]
      if (!destinationModule) {
        return
      }
      // find the dragged topic
      const topic = sourceModule.topics?.find(
        (topic) => topic.id === sourceData.topicId,
      )
      // console.log(`topic`, topic)
      if (!topic) {
        return
      }
      // add the topic to the destination module with correct positions
      const topicIds = destinationModule.topics?.map((topic) => topic.id)

      // find the module index in the refDragAndDropDataDict
      let moduleIndex = Object.values(refDragAndDropDataDict.current).findIndex(
        (data) => data.moduleId === destinationModuleId,
      )
      if (moduleIndex === -1) {
        return
      }
      // skip the first item, so -2
      const sourceIndex = source.index - moduleIndex - 2
      const destinationIndex = destination.index - moduleIndex - 2
      // insert draggedTopicId into topicIds
      if (sourceIndex < destinationIndex) {
        topicIds.push(topic.id)
      } else {
        topicIds.splice(destinationIndex, 0, topic.id)
      }

      // reorder the topic ids
      // console.log(`sourceIndex`, sourceIndex)
      // console.log(`targetIndex`, targetIndex)
      // console.log(`before reordering topics`, topicIds)
      const newTopicIds = reorderTopics(
        topicIds,
        sourceTopicId,
        destinationTopicId,
      )
      // console.log(`after reordering topics`, newTopicIds)

      // update local state
      // add the dragged topic
      destinationModule.topics = [
        ...destinationModule.topics,
        {
          ...topic,
        },
      ]

      destinationModule.topics.sort(
        (a, b) => newTopicIds.indexOf(a.id) - newTopicIds.indexOf(b.id),
      )

      // remove the topic from the source module
      sourceModule.topics = sourceModule.topics?.filter(
        (topic) => topic.id !== sourceData.topicId,
      )

      // move topic to the destination module
      await moveTopicToModule(sourceData.topicId, destinationModuleId)
      // update module positions
      await updateArchModule(destinationModuleId, undefined, newTopicIds)
    }
  }

  const clearAllCheckboxes = () => {
    setCheckedCourseIds([])
    setCheckedModuleIds([])
    setCheckedTopicIds([])
    setCheckedCourseModulesIds([])
    setCheckedCourseAssessmentsIds([])
    setCheckedQuizIds([])
  }

  const onSelectAll = (checked: boolean) => {
    setCheckboxSelectAll(checked)

    if (!checked) {
      clearAllCheckboxes()
    }

    if (!selectedClient) {
      return
    }

    // selected courses, checked all courses in the same program
    if (checked && checkedCourseIds.length > 0) {
      // find the program
      const program = selectedClient.programs?.find((program) =>
        program.intakes?.find((intake) =>
          intake.courses?.find((course) => course.id === checkedCourseIds[0]),
        ),
      )
      if (program) {
        const courseIds = program.intakes
          ?.map((intake) => intake.courses?.map((course) => course.id))
          .flat()
          .filter(Boolean)
        setCheckedCourseIds(courseIds)
      }
    } else if (checked && checkedModuleIds.length > 0) {
      // selected modules, checked all modules in the same course
      const lastModuleId = checkedModuleIds[checkedModuleIds.length - 1]
      const course = selectedClient.programs
        ?.find((program) =>
          program.intakes?.find((intake) =>
            intake.courses?.find((course) =>
              course.modules?.find((module) => module.id === lastModuleId),
            ),
          ),
        )
        ?.intakes?.find((intake) =>
          intake.courses?.find((course) =>
            course.modules?.find((module) => module.id === lastModuleId),
          ),
        )
        ?.courses?.find((course) =>
          course.modules?.find((module) => module.id === lastModuleId),
        )
      if (course) {
        const moduleIds = course.modules?.map((module) => module.id)
        setCheckedModuleIds(moduleIds)
      }
    } else if (checked && checkedTopicIds.length > 0) {
      // selected topics, checked all topics in the same module
      const lastTopicId = checkedTopicIds[checkedTopicIds.length - 1]
      const module = selectedClient.programs
        ?.find((program) =>
          program.intakes?.find((intake) =>
            intake.courses?.find((course) =>
              course.modules?.find((module) =>
                module.topics?.find((topic) => topic.id === lastTopicId),
              ),
            ),
          ),
        )
        ?.intakes?.find((intake) =>
          intake.courses?.find((course) =>
            course.modules?.find((module) =>
              module.topics?.find((topic) => topic.id === lastTopicId),
            ),
          ),
        )
        ?.courses?.find((course) =>
          course.modules?.find((module) =>
            module.topics?.find((topic) => topic.id === lastTopicId),
          ),
        )
        ?.modules?.find((module) =>
          module.topics?.find((topic) => topic.id === lastTopicId),
        )
      if (module) {
        const topicIds = module.topics?.map((topic) => topic.id)
        setCheckedTopicIds(topicIds)
      }
    } else if (checked && checkedCourseModulesIds.length > 0) {
      // selected course modules, checked all modules in the same course
      const lastCourseId =
        checkedCourseModulesIds[checkedCourseModulesIds.length - 1]
      const course = selectedClient.programs
        ?.find((program) =>
          program.intakes?.find((intake) =>
            intake.courses?.find((course) => course.id === lastCourseId),
          ),
        )
        ?.intakes?.find((intake) =>
          intake.courses?.find((course) => course.id === lastCourseId),
        )
        ?.courses?.find((course) => course.id === lastCourseId)
      if (course) {
        const moduleIds = course.modules?.map((module) => module.id)
        setCheckedModuleIds(moduleIds)
      }
    } else if (checked && checkedCourseAssessmentsIds.length > 0) {
      // selected course assessments, checked all quizzes in the same course
      const lastCourseId =
        checkedCourseAssessmentsIds[checkedCourseAssessmentsIds.length - 1]
      const course = selectedClient.programs
        ?.find((program) =>
          program.intakes?.find((intake) =>
            intake.courses?.find((course) => course.id === lastCourseId),
          ),
        )
        ?.intakes?.find((intake) =>
          intake.courses?.find((course) => course.id === lastCourseId),
        )
        ?.courses?.find((course) => course.id === lastCourseId)
      console.log(`course`, course)
      if (course) {
        const quizIds = course.quizzes?.map((quiz) => quiz.id)
        setCheckedQuizIds(quizIds)
      }
    }
  }

  const onUpdatedArchTopic = useCallback(
    (data: { topic: ArchTopic }) => {
      // TODO: update the topic in the list locally
      if (!selectedClient) {
        return
      }
      fetchClient(selectedClient.id, true)
    },
    [fetchClient, selectedClient],
  )

  const onReloadClient = useCallback(() => {
    if (!selectedClient) return
    fetchClient(selectedClient.id, true)
  }, [fetchClient, selectedClient])

  const onCopiedObject = useCallback(
    async (data: {
      clientId?: string
      programId?: string
      intakeId?: string
      courseId?: string
      courseIds?: string[]
      moduleId?: string
      moduleIds?: string[]
      topicId?: string
      topicIds?: string[]
    }) => {
      // console.log(`onCopiedObject`, data)
      if (!selectedClient || !data.clientId) return
      if (selectedClient.id === data.clientId) {
        // set copied object data to refClonedObject
        refclonedObject.current = data

        await fetchClient(selectedClient.id, true)
        // console.log('Copied object is in the same client')
      } else {
        // reset
        refclonedObject.current = {}
      }
    },
    [fetchClient, selectedClient],
  )

  const onSaveAndExit = useCallback(async () => {
    // find all program add input by id
    const addInput = document.getElementById('arch-add-program-input')
    // check if there is any not empty value, if yes, save it
    if (addInput) {
      // convert to html input element
      const addInputEl = addInput as HTMLInputElement
      const value = addInputEl.value.trim()
      if (value) {
        createProgram(value)
      }
    }

    // find all intake add input by class name, filter no id
    const addIntakeInputs = document.getElementsByClassName(
      'arch-add-intake-input',
    )
    // check if there is any not empty value, if yes, save it
    for (let i = 0; i < addIntakeInputs.length; i++) {
      const addIntakeInput = addIntakeInputs[i] as HTMLInputElement
      const value = addIntakeInput.value.trim()
      if (value) {
        const programId = addIntakeInput.dataset.programId
        if (programId) {
          // console.log(`programId`, programId)
          createIntake(programId, value)
        }
      }
    }

    // find all course add input by class name, filter no id
    const addCourseInputs = document.getElementsByClassName(
      'arch-add-course-input',
    )
    // check if there is any not empty value, if yes, save it
    for (let i = 0; i < addCourseInputs.length; i++) {
      const addCourseInput = addCourseInputs[i] as HTMLInputElement
      const value = addCourseInput.value.trim()
      if (value) {
        const intakeId = addCourseInput.dataset.intakeId
        if (intakeId) {
          // console.log(`intakeId`, intakeId)
          createCourse(intakeId, value)
        }
      }
    }

    // find all module add input by class name, filter no id
    const addModuleInputs = document.getElementsByClassName(
      'arch-add-module-input',
    )
    // check if there is any not empty value, if yes, save it
    for (let i = 0; i < addModuleInputs.length; i++) {
      const addModuleInput = addModuleInputs[i] as HTMLInputElement
      const value = addModuleInput.value.trim()
      if (value) {
        const courseId = addModuleInput.dataset.courseId
        if (courseId) {
          // console.log(`courseId`, courseId)
          createModule(courseId, value)
        }
      }
    }

    // find all topic add input by class name, filter no id
    const addTopicInputs = document.getElementsByClassName(
      'arch-add-topic-input',
    )
    // check if there is any not empty value, if yes, save it
    for (let i = 0; i < addTopicInputs.length; i++) {
      const addTopicInput = addTopicInputs[i] as HTMLInputElement
      const value = addTopicInput.value.trim()
      if (value) {
        const moduleId = addTopicInput.dataset.moduleId
        if (moduleId) {
          // console.log(`moduleId`, moduleId)
          createTopic(moduleId, value)
        }
      }
    }
  }, [createCourse, createIntake, createModule, createProgram, createTopic])

  const onAddedAssessments = useCallback(() => {
    if (!selectedClient) return
    // TODO: update the client in the list locally
    fetchClient(selectedClient.id, true)
  }, [fetchClient, selectedClient])

  useEffect(() => {
    if (isLogged && client) {
      fetchClient(client.id, true)
    }
  }, [isLogged, fetchClient, client])

  useEffect(() => {
    Emitter.on(Events.UpdatedArchTopic, onUpdatedArchTopic)
    Emitter.on(Events.ReloadArchClient, onReloadClient)
    Emitter.on(Events.CopiedArchObject, onCopiedObject)
    Emitter.on(Events.ArchSaveAndExit, onSaveAndExit)
    Emitter.on(Events.AddedUnassignedAssignments, onAddedAssessments)
    return () => {
      Emitter.off(Events.UpdatedArchTopic, onUpdatedArchTopic)
      Emitter.off(Events.ReloadArchClient, onReloadClient)
      Emitter.off(Events.CopiedArchObject, onCopiedObject)
      Emitter.off(Events.ArchSaveAndExit, onSaveAndExit)
      Emitter.off(Events.AddedUnassignedAssignments, onAddedAssessments)
    }
  }, [
    onAddedAssessments,
    onCopiedObject,
    onReloadClient,
    onSaveAndExit,
    onUpdatedArchTopic,
  ])

  return (
    <>
      <div>
        {(isFetchingClient || isLoading) && <Spinner />}
        {!isFetchingClient && selectedClient && (
          <>
            {/* Confirmation */}
            {canCreateDeleteClone &&
              (checkedActionType() !== null ||
                (showDeleteConfirmation && singleDelete !== null)) && (
                <div
                  className="mb-[18px] flex min-h-[36px] flex-row items-center gap-6"
                  id="arch-delete-confirmation"
                  data-testid="arch-delete-confirmation"
                >
                  {showDeleteConfirmation &&
                    (checkedActionType() !== null || singleDelete !== null) && (
                      <>
                        <span className="mentem-action-text">
                          {renderDeleteConfirmationText()}
                        </span>
                        <button
                          data-testid="btn-confirm-delete"
                          className={StyleUtil.buttonPrimaryUnsw}
                          onClick={() => {
                            onConfirmDelete()
                          }}
                          disabled={isDeleting}
                        >
                          {isDeleting ? <ButtonSpinner /> : 'Yes'}
                        </button>
                        <button
                          data-testid="btn-cancel-delete"
                          className="mentem-action-cancel"
                          onClick={() => setShowDeleteConfirmation(false)}
                        >
                          Cancel
                        </button>
                        {checkedActionType() !== null && (
                          <>
                            <span className="mentem-action-or">or</span>
                            <button
                              data-testid="btn-clear-selection"
                              className="mentem-action-clear"
                              onClick={() => {
                                setShowDeleteConfirmation(false)
                                clearAllCheckboxes()
                              }}
                            >
                              Clear selection
                            </button>
                          </>
                        )}
                      </>
                    )}
                  {!showDeleteConfirmation && checkedActionType() !== null && (
                    <>
                      <Checkbox
                        id="checkbox-arch-select-all"
                        data-testid="checkbox-arch-select-all"
                        checked={checkboxSelectAll}
                        onChange={(checked) => onSelectAll(checked)}
                        size={CheckboxSize.Medium}
                        theme={CheckboxTheme.Normal}
                      />
                      <label
                        htmlFor="checkbox-arch-select-all"
                        className="mentem-action-text ml-[-8px]"
                        onClick={() => onSelectAll(!checkboxSelectAll)}
                      >
                        {renderAllSelectText()}
                      </label>
                      <span
                        data-testid="btn-delete-selected"
                        className="mentem-action-delete"
                        onClick={() => setShowDeleteConfirmation(true)}
                      >
                        {renderDeleteFromText()}
                      </span>
                      <span
                        data-testid="btn-duplicate-selected"
                        className="arch-duplicate-text"
                        onClick={onDuplicateSelectedItems}
                      >
                        {renderDuplicateToText()}
                      </span>
                    </>
                  )}
                </div>
              )}
            {/* Architecture editor list */}
            <div className="mentem-scrollbar-2 max-h-[50vh] overflow-y-auto">
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="arch-list">
                  {(provided) => (
                    <ul
                      className="arch-editor-list divide-slate-200 divide-y"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      <li
                        key="programs"
                        className="arch-editor-list-title arch-header-list-item"
                        style={{
                          ...unswoHeaderStyle,
                          ...(!isEnterprise && { background: UNSWO_ROOT_BG }),
                        }}
                      >
                        <div>
                          <div
                            className="w-[70%]"
                            style={{ paddingLeft: nestingPadding(0) }}
                          >
                            {`${getEntityLabel(
                              ArchActionType.Program,
                              !isEnterprise,
                            )}(s):`}
                          </div>
                          <div className="flex w-[30%] justify-end">
                            {renderActionButtons(
                              ArchActionType.Client,
                              selectedClient.id,
                            )}
                          </div>
                        </div>
                      </li>
                      {selectedClient.programs &&
                        !collapsedClientIds.includes(selectedClient.id) &&
                        selectedClient.programs
                          .filter(
                            (p) =>
                              (filteredProgramName === undefined ||
                                p.name === filteredProgramName) &&
                              (filteredIntakeName === undefined ||
                                p.intakes.some(
                                  (i) => i.name === filteredIntakeName,
                                )),
                          )
                          .map((program, index) =>
                            buildProgramRow(program, index),
                          )}
                      {showAddProgram && (
                        <li
                          key="add-program"
                          className="arch-program-list-item"
                        >
                          <AddEntityInput
                            entityName={getEntityLabel(
                              ArchActionType.Program,
                              !isEnterprise,
                            )}
                            isAdding={isAddingProgram}
                            onConfirm={createProgram}
                            onCancel={() => setShowAddProgram(false)}
                            onKeyDown={onKeyDownForTextArea}
                            testId="input-add-program"
                            wrapperStyle={{ paddingLeft: nestingPadding(0) }}
                          />
                        </li>
                      )}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </>
        )}
      </div>
      {/* Tooltips */}
      <Tooltip
        id="tooltip-upload-excel"
        className="mentem-tooltip mini-tooltip"
        style={tooltipStyle}
        place="top"
        noArrow={true}
      >
        Upload excel
      </Tooltip>
      <Tooltip
        id="tooltip-move-up-topic"
        className="mentem-tooltip mini-tooltip"
        style={tooltipStyle}
        place="top"
        noArrow={true}
      >
        Move up
      </Tooltip>
      <Tooltip
        id="tooltip-move-down-topic"
        className="mentem-tooltip mini-tooltip"
        style={tooltipStyle}
        place="top"
        noArrow={true}
      >
        Move down
      </Tooltip>
      <Tooltip
        id="tooltip-move-up-module"
        className="mentem-tooltip mini-tooltip"
        style={tooltipStyle}
        place="top"
        noArrow={true}
      >
        Move up
      </Tooltip>
      <Tooltip
        id="tooltip-move-down-module"
        className="mentem-tooltip mini-tooltip"
        style={tooltipStyle}
        place="top"
        noArrow={true}
      >
        Move down
      </Tooltip>
      <Tooltip
        id="tooltip-move-topic"
        className="mentem-tooltip mini-tooltip"
        style={tooltipStyle}
        place="top"
        noArrow={true}
      >
        Move topic
      </Tooltip>

      {/* Generate tooltips for all action types */}
      {[
        ArchActionType.Client,
        ArchActionType.Program,
        ArchActionType.Intake,
        ArchActionType.Course,
        ArchActionType.Module,
        ArchActionType.Topic,
        ArchActionType.CourseModules,
        ArchActionType.CourseAssessments,
        ArchActionType.CourseAssessmentsTests,
        ArchActionType.Quiz,
      ].map((type, index) => {
        const { id, displayName } = getTooltipConfig(type, !isEnterprise)
        const actions = ['add', 'copy', 'delete', 'expand', 'collapse']

        return (
          <React.Fragment key={index}>
            {actions.map((action) => (
              <Tooltip
                key={action}
                id={`tooltip-${action}-${id}`}
                className="mentem-tooltip mini-tooltip"
                style={tooltipStyle}
                place="top"
                noArrow={true}
              >
                {action === 'copy'
                  ? `Duplicate ${displayName}`
                  : `${
                      action.charAt(0).toUpperCase() + action.slice(1)
                    } ${displayName}`}
              </Tooltip>
            ))}
          </React.Fragment>
        )
      })}
    </>
  )
}
